import { z } from 'zod';
import { emailMeTool } from './email-tool';

export type AgentToolName = string;

/**
 * Name of the server-executed meta-tool that activates additional toolsets.
 * It is defined in server/api/agent/chat.post.ts (with an execute function),
 * not in this registry — the client-side executor must ignore calls to it,
 * since its output streams from the server.
 */
export const OPEN_TOOLSET_TOOL_NAME = 'open_toolset';

export interface AgentToolDef {
  name: AgentToolName;
  /** Hand-tuned; this drives tool-selection quality */
  description: string;
  /** Which API base URL the tool targets; defaults to 'admin' when omitted */
  api?: 'admin' | 'legacy';
  /** Grouping tag for dynamic tool exposure (prepareStep activeTools) */
  toolset: 'core' | 'staff' | 'timeline' | 'money' | 'case-detail' | 'scheduling' | 'comms' | 'reference' | 'oversight' | 'wellsky';
  inputSchema: z.ZodObject<any>;
  /** Omitted for client-handled tools (see CLIENT_TOOL_HANDLERS in agent-tool-executor.ts) */
  method?: 'GET' | 'POST' | 'PUT';
  /** Build the Admin API request from validated LLM input; omitted for client-handled tools */
  buildRequest?: (input: any) => { path: string; query?: Record<string, any>; body?: any };
  /** TsammPermission gate — tool omitted from LLM schema set if user lacks it */
  requiredPermission?: string;
  /** Mutations require an in-chat human confirmation card before execution */
  requiresConfirmation?: boolean;
  /** Optional post-processor to shrink/shape API responses before they hit the context window */
  selectResult?: (raw: any) => any;
  /**
   * Runs before the confirmation card opens (read-only calls: it must not send or write anything).
   * Its result is shown on the card; `{ error }` skips the card and is returned to the model.
   */
  prepare?: (input: any, ctx: AgentPrepareContext) => Promise<AgentPrepared>;
  /** What the confirmation card shows for this tool; without it the card lists the raw input */
  confirm?: AgentConfirmSpec;
  /** Extra body keys only the USER may add on the card (never the model); e.g. a test recipient */
  extraBodyKeys?: string[];
  /** Shown when the API does not have the endpoint yet (404) */
  notFoundMessage?: string;
}

/** Read-only API access for `prepare`; same auth and assistant tagging as a tool call. */
export interface AgentPrepareContext {
  get: (path: string) => Promise<AgentApiResult>;
  /** POST to a route that renders and returns data without sending or writing anything */
  preview: (path: string, body: unknown) => Promise<AgentApiResult>;
}

export interface AgentApiResult {
  ok: boolean;
  status: number;
  message: string;
  data: any;
}

export interface AgentPrepared {
  error?: string;
  sideEffects?: string[];
  fields?: Array<{ label: string; value: string }>;
  /** Email tool: the email exactly as the API would send it (sandboxed preview on the card) */
  emailPreview?: { subject: string; html: string; recipientMasked: string | null } | null;
  /** Email tool: whether a tester may name another recipient on this server */
  emailOverride?: { allowed: boolean; allowedDomains: string[] };
}

export interface AgentConfirmSpec {
  title: (input: any, prepared?: AgentPrepared) => string;
  confirmLabel?: string;
  sideEffects?: (input: any, prepared?: AgentPrepared) => string[];
  fields?: (input: any, prepared?: AgentPrepared) => Array<{ label: string; value: string }>;
}

// ---------------------------------------------------------------------------
// Shared helpers
// Admin API list endpoints bind [FromQuery] List<FilterParams> from indexed
// query keys: filters[i].Field / filters[i].Value / filters[i].Operation
// (+ optional filters[i].Label), alongside page / pageSize / sort / order.
// ---------------------------------------------------------------------------

type FilterQueryBuilder = {
  query: Record<string, any>;
  add: (field: string, value: unknown, operation: string, label?: string) => void;
};

const filterQuery = (base: Record<string, any> = {}): FilterQueryBuilder => {
  const query = base;
  let i = 0;
  return {
    query,
    add(field, value, operation, label) {
      query[`filters[${i}].Field`] = field;
      if (label) query[`filters[${i}].Label`] = label;
      query[`filters[${i}].Value`] = value;
      query[`filters[${i}].Operation`] = operation;
      i++;
    },
  };
};

const userDisplayName = (user: any) =>
  user ? user.rankedUserName || [user.firstName, user.lastName].filter(Boolean).join(' ') : undefined;

// ---------------------------------------------------------------------------
// Applicants domain tools
// Endpoints: ApplicantsController (api/v1/applicants) in the .NET Admin API.
// ---------------------------------------------------------------------------

const MAX_PAGE_SIZE = 20;

const trimApplicantRow = (a: any) => ({
  id: a.id,
  applicantIdPublic: a.idPublic,
  firstName: a.firstName,
  lastName: a.lastName,
  emailAddress: a.emailAddress,
  phoneNumber: a.phoneNumber,
  city: a.city,
  state: a.state,
  zipcode: a.zipcode,
  serviceAreaNames: a.serviceAreaNames,
  totalAssistanceRequests: a.totalAssistanceRequests,
  openAssistanceRequests: a.openAssistanceRequests,
  hasOpenRequests: a.hasOpenRequests,
  createdAt: a.createdAt,
  updatedAt: a.updatedAt,
});

const applicantsTools: AgentToolDef[] = [
  {
    name: 'search_applicants',
    toolset: 'core',
    description:
      'Search the applicant list, automatically scoped to the service areas the current user can access. Use when looking for applicants by name, email, phone, location, public applicant ID, or activity (open/total assistance requests, created/updated date ranges). Returns a paged result: total match count plus up to pageSize applicant rows (ids, name, contact info, city/state/zip, service areas, request counts, dates). Use get_applicant with the returned id for full detail. Prefer find_applicant_by_email when you have an exact email address. ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich clickable table — do NOT repeat the rows as a markdown table or list; reply with a one-line summary and any insight instead.',
    inputSchema: z.object({
      applicantIdPublic: z
        .number()
        .int()
        .optional()
        .describe('Exact public applicant ID (the numeric ID staff see in the UI, e.g. 10234). Exact match.'),
      firstName: z.string().optional().describe('First name, partial match (contains, case-insensitive).'),
      lastName: z.string().optional().describe('Last name, partial match (contains, case-insensitive).'),
      emailAddress: z.string().optional().describe('Email address, partial match (contains).'),
      phoneNumber: z
        .string()
        .optional()
        .describe('Phone number, partial match (contains). Digits work best, e.g. "5125551234" or "555".'),
      city: z.string().optional().describe('City, partial match (contains).'),
      state: z.string().optional().describe('State, partial match (contains), e.g. "TX".'),
      zipcode: z
        .string()
        .optional()
        .describe('Zip/postal code(s). Comma-separate multiple codes to match any of them, e.g. "78701,78702".'),
      serviceAreaIds: z
        .array(z.uuid())
        .optional()
        .describe('Restrict to these service area GUIDs. Resolve names to ids with list_service_areas first.'),
      hasOpenRequests: z
        .boolean()
        .optional()
        .describe('true = only applicants with at least one open assistance request. Omit to not filter on this.'),
      createdAfter: z
        .string()
        .optional()
        .describe('Only applicants created on/after this UTC ISO timestamp, e.g. "2026-01-01T00:00:00Z".'),
      createdBefore: z.string().optional().describe('Only applicants created on/before this UTC ISO timestamp.'),
      updatedAfter: z.string().optional().describe('Only applicants last updated on/after this UTC ISO timestamp.'),
      updatedBefore: z.string().optional().describe('Only applicants last updated on/before this UTC ISO timestamp.'),
      minTotalAssistanceRequests: z
        .number()
        .int()
        .optional()
        .describe('Only applicants with at least this many total assistance requests.'),
      maxTotalAssistanceRequests: z
        .number()
        .int()
        .optional()
        .describe('Only applicants with at most this many total assistance requests.'),
      minOpenAssistanceRequests: z
        .number()
        .int()
        .optional()
        .describe('Only applicants with at least this many open assistance requests.'),
      maxOpenAssistanceRequests: z
        .number()
        .int()
        .optional()
        .describe('Only applicants with at most this many open assistance requests.'),
      page: z.number().int().min(1).optional().describe('1-based page number. Default 1.'),
      pageSize: z
        .number()
        .int()
        .min(1)
        .max(MAX_PAGE_SIZE)
        .optional()
        .describe(`Rows per page, max ${MAX_PAGE_SIZE}. Default 10. Use the returned count to decide whether to page or narrow the filters.`),
      sort: z
        .enum([
          'idPublic',
          'firstName',
          'lastName',
          'emailAddress',
          'city',
          'state',
          'zipcode',
          'totalAssistanceRequests',
          'openAssistanceRequests',
          'createdAt',
          'updatedAt',
        ])
        .optional()
        .describe('Sort field. Default "createdAt".'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction. Default "desc".'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const { query, add } = filterQuery();
      if (input.applicantIdPublic != null) add('idPublic', input.applicantIdPublic, 'IsEqualTo');
      if (input.firstName) add('firstName', input.firstName, 'Contains');
      if (input.lastName) add('lastName', input.lastName, 'Contains');
      if (input.emailAddress) add('emailAddress', input.emailAddress, 'Contains');
      if (input.phoneNumber) add('phoneNumber', input.phoneNumber, 'Contains');
      if (input.city) add('city', input.city, 'Contains');
      if (input.state) add('state', input.state, 'Contains');
      if (input.zipcode) add('zipcode', input.zipcode, 'ContainsAny');
      if (input.serviceAreaIds?.length) add('serviceAreaIds', input.serviceAreaIds.join(','), 'In');
      if (input.hasOpenRequests != null) add('hasOpenRequests', input.hasOpenRequests, 'IsEqualTo');
      if (input.createdAfter) add('createdAt', input.createdAfter, 'IsGreaterThanOrEqualTo');
      if (input.createdBefore) add('createdAt', input.createdBefore, 'IsLessThanOrEqualTo');
      if (input.updatedAfter) add('updatedAt', input.updatedAfter, 'IsGreaterThanOrEqualTo');
      if (input.updatedBefore) add('updatedAt', input.updatedBefore, 'IsLessThanOrEqualTo');
      if (input.minTotalAssistanceRequests != null)
        add('totalAssistanceRequests', input.minTotalAssistanceRequests, 'IsGreaterThanOrEqualTo');
      if (input.maxTotalAssistanceRequests != null)
        add('totalAssistanceRequests', input.maxTotalAssistanceRequests, 'IsLessThanOrEqualTo');
      if (input.minOpenAssistanceRequests != null)
        add('openAssistanceRequests', input.minOpenAssistanceRequests, 'IsGreaterThanOrEqualTo');
      if (input.maxOpenAssistanceRequests != null)
        add('openAssistanceRequests', input.maxOpenAssistanceRequests, 'IsLessThanOrEqualTo');
      query.page = input.page ?? 1;
      query.pageSize = Math.min(input.pageSize ?? 10, MAX_PAGE_SIZE);
      query.sort = input.sort ?? 'createdAt';
      query.order = input.order ?? 'desc';
      return { path: '/applicants', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      applicants: (raw?.result ?? []).map(trimApplicantRow),
    }),
  },
  {
    name: 'get_applicant',
    toolset: 'core',
    description:
      'Get one applicant record in full by internal GUID (the "id" returned by search_applicants or find_applicant_by_email — not the numeric public ID). Returns personal details (name, DOB, contact info, address, county, income), validation flags (email/phone validated, opted in), timestamps, external integration ids, and a summary of every assistance request on file (type, status, amounts, service area, key dates). Use this before answering any detailed question about a specific applicant.',
    inputSchema: z.object({
      id: z.uuid().describe('Applicant GUID (internal id). If you only have a numeric public ID or a name, use search_applicants first.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applicants/${input.id}` }),
    selectResult: (raw) =>
      raw && {
        id: raw.id,
        applicantIdPublic: raw.idPublic,
        firstName: raw.firstName,
        middleName: raw.middleName,
        lastName: raw.lastName,
        suffix: raw.suffix,
        dateOfBirth: raw.dateOfBirth,
        emailAddress: raw.emailAddress,
        emailValidated: raw.emailValidated,
        phoneNumber: raw.phoneNumber,
        phoneValidated: raw.phoneValidated,
        phoneOptedIn: raw.phoneOptedIn,
        address1: raw.address1,
        address2: raw.address2,
        city: raw.city,
        state: raw.state,
        zipcode: raw.zipcode,
        county: raw.county,
        totalEarnedIncome: raw.totalEarnedIncome,
        createdAt: raw.createdAt,
        updatedAt: raw.updatedAt,
        clientIds: raw.clientIds,
        householdIds: raw.householdIds,
        assistanceRequests: (raw.assistanceRequests ?? []).map((ar: any) => ({
          id: ar.id,
          requestIdPublic: ar.idPublic,
          assistanceType: ar.assistanceType?.name,
          status: ar.status,
          amountRequested: ar.amountRequested,
          amountApproved: ar.amountApproved,
          serviceArea: ar.serviceArea?.name,
          payee: ar.payee,
          createdAt: ar.createdAt,
          submittedAt: ar.submittedAt,
          approvalDate: ar.approvalDate,
          nextEligibleDate: ar.nextEligibleDate,
        })),
      },
  },
  {
    name: 'find_applicant_by_email',
    toolset: 'core',
    description:
      'Look up a single applicant by their exact email address (case-insensitive, full address — not a partial match). Returns name, phone, address with coordinates, email-validated flag, and the applicant\'s most recent service area and licensed location; returns null when no applicant has that email. Use this as the fastest path when the user provides an email. Note: it does NOT return the applicant GUID — follow up with search_applicants (emailAddress filter) if you need the id for get_applicant.',
    inputSchema: z.object({
      email: z.email().describe('Exact, complete email address of the applicant, e.g. "jane.doe@example.com".'),
    }),
    method: 'POST',
    buildRequest: (input) => ({ path: '/applicants/details', body: { email: input.email } }),
  },
  {
    name: 'get_applicant_history',
    toolset: 'core',
    description:
      'Get the full change-history/audit trail for an applicant: profile edits, status events, and system actions, each with a category, human-readable message, the staff user who acted, and a timestamp. Use to answer "what changed on this applicant and when" or "who edited this record". Returns the complete list (not paged). For staff-written comments specifically, prefer get_applicant_comments.',
    inputSchema: z.object({
      id: z.uuid().describe('Applicant GUID (from search_applicants or get_applicant).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applicants/${input.id}/history` }),
    selectResult: (raw) =>
      (raw ?? []).map((h: any) => ({
        id: h.id,
        category: h.category,
        message: h.message,
        by: userDisplayName(h.user),
        createdAt: h.createdAt,
      })),
  },
  {
    name: 'get_applicant_comments',
    toolset: 'core',
    description:
      'Get staff comments left on an applicant record, paged and sorted newest first. Each comment has the message text, the staff user who wrote it, and a timestamp. Use when asked about case notes or remarks on an applicant. Returns total comment count plus up to pageSize comments.',
    inputSchema: z.object({
      id: z.uuid().describe('Applicant GUID (from search_applicants or get_applicant).'),
      page: z.number().int().min(1).optional().describe('1-based page number. Default 1.'),
      pageSize: z
        .number()
        .int()
        .min(1)
        .max(MAX_PAGE_SIZE)
        .optional()
        .describe(`Comments per page, max ${MAX_PAGE_SIZE}. Default 10.`),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/applicants/${input.id}/comments`,
      query: {
        page: input.page ?? 1,
        pageSize: Math.min(input.pageSize ?? 10, MAX_PAGE_SIZE),
        sort: 'createdAt',
        order: 'desc',
      },
    }),
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      comments: (raw?.result ?? []).map((c: any) => ({
        id: c.id,
        message: c.message,
        by: userDisplayName(c.user),
        createdAt: c.createdAt,
      })),
    }),
  },
  {
    name: 'add_applicant_comment',
    toolset: 'core',
    description:
      'Add a staff comment to an applicant record. This is a WRITE action: it permanently appends a note (attributed to the current user) to the applicant\'s comment history, visible to all staff with access. Use only when the user explicitly asks to add a note/comment to an applicant. Returns a success confirmation message.',
    inputSchema: z.object({
      id: z.uuid().describe('Applicant GUID to attach the comment to (from search_applicants or get_applicant).'),
      message: z
        .string()
        .min(1)
        .describe('The comment text exactly as it should appear in the record. Plain text; keep it factual and concise.'),
    }),
    method: 'POST',
    requiresConfirmation: true,
    requiredPermission: 'SAHelpCaseManagement',
    buildRequest: (input) => ({
      path: `/applicants/${input.id}/comments`,
      // 'Added Comment: ' prefix mirrors the admin UI so agent comments render identically in history
      body: { message: 'Added Comment: ' + input.message },
    }),
  },
];

// ---------------------------------------------------------------------------
// Cases (assistance requests) domain tools
// Endpoints: AssistanceRequestsController / ApplicationsController.
// ---------------------------------------------------------------------------

const CASE_FILTER_OPERATIONS = [
  'IsEqualTo',
  'IsNotEqualTo',
  'IsGreaterThanOrEqualTo',
  'IsLessThanOrEqualTo',
  'In',
  'Contains',
  'ContainsAny',
] as const;

const CASE_FILTER_FIELDS = [
  'idPublic',
  'applicationIdPublic',
  'firstName',
  'lastName',
  'status',
  'serviceAreaId',
  'assistanceTypeId',
  'assignedTo',
  'county',
  'zipCode',
  'payee',
  'amountRequested',
  'amountApproved',
  'createdAt',
  'updatedAt',
  'totalHouseholdMembers',
  'arsInApplication',
  'funds',
  'fundingSources',
  'pathwayOfHope',
  'submittedLanguageId',
  'declinedReasonId',
] as const;

const CASE_SORT_COLUMNS = [
  'createdAt',
  'updatedAt',
  'submittedAt',
  'status',
  'lastName',
  'amountRequested',
  'amountApproved',
  'idPublic',
] as const;

// Always kept on each search result row (when present in the response)
// Field names of AssistanceRequestFilterViewDto (GET /assistance-requests)
const CASE_ROW_FIELDS = [
  'id',
  'idPublic',
  'applicationId',
  'applicationIdPublic',
  'applicantId',
  'applicantIdPublic',
  'firstName',
  'lastName',
  'status',
  'assistanceTypeDescription',
  'assistanceTypeId',
  'serviceAreaId',
  'serviceAreaName',
  'assignedTo',
  'amountRequested',
  'amountApproved',
  'createdAt',
  'submittedAt',
  'updatedAt',
  'payee',
  'fundingSources',
  'funds',
  'zipCode',
  'county',
  'totalHouseholdMembers',
  'arsInApplication',
] as const;

// Optional extra columns; kept on rows only when non-empty
const CASE_ROW_EXTRAS = [
  'emailAddress',
  'submittedLanguage',
  'pathwayOfHope',
  'declinedReasonText',
] as const;

const casesPick = (obj: any, keys: readonly string[]) => {
  const out: Record<string, any> = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const k of keys) if (obj[k] !== undefined) out[k] = obj[k];
  return out;
};

const casesPickNonEmpty = (obj: any, keys: readonly string[]) => {
  const out: Record<string, any> = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) out[k] = v;
  }
  return out;
};

const casesTrimApplicant = (applicant: any) =>
  casesPickNonEmpty(applicant, [
    'id',
    'idPublic',
    'firstName',
    'middleName',
    'lastName',
    'suffix',
    'emailAddress',
    'phoneNumber',
    'dateOfBirth',
    'address1',
    'address2',
    'city',
    'state',
    'zipcode',
    'county',
    'createdAt',
    'updatedAt',
  ]);

const casesTrimServiceArea = (sa: any) => casesPickNonEmpty(sa, ['id', 'name', 'licensedLocationId', 'timeZone']);

const casesFilterValueSchema = z
  .union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
  .describe(
    'Value to compare against. Use an array with the In / ContainsAny operations (it is sent comma-joined). ' +
      'Money fields are integer cents ($50.00 = 5000). Date fields take UTC ISO-8601 values ' +
      '(YYYY-MM-DD or full timestamp); for an "on or before <date>" bound, pass the start of the NEXT day with IsLessThanOrEqualTo so the whole day is included.'
  );

const casesTools: AgentToolDef[] = [
  {
    name: 'search_cases',
    toolset: 'core',
    description:
      'Search the assistance-request (case) queue. This is the primary tool for any question about cases: ' +
      'counts by status ("how many pending cases?"), lists ("show declined cases this month"), lookups by public ID ' +
      '(AR-… numbers), cases assigned to a staff member, cases for an applicant name/zip, amounts requested/approved, etc. ' +
      'Returns { count, cases } — count is the TOTAL matching cases across all pages (for how-many questions read count and use pageSize 1); ' +
      'cases is one page of rows with GUID ids (use with get_case / assign_case / navigate_to), numeric public IDs, applicant name, status, ' +
      'assistance type, service area, amounts in integer cents, and created/submitted/updated dates. ' +
      'Results are automatically restricted to the service areas the current user can access. ' +
      'Draft, abandoned and user-deleted cases are EXCLUDED unless the matching include flag is set. ' +
      'Status meanings: ApprovalPending = submitted for approval; Approved = approved; ApprovedReceivedByFinance = "Received by Finance"; ' +
      'ApprovedFundingSent = "Funded"; ApprovedClosed = "Paid"; ReturnedToApplicant = sent back for changes. ' +
      'To filter by service area or assistance type NAME, first resolve the name to a GUID with list_service_areas / list_assistance_types. ' +
      'Combine multiple filters for AND logic; use two range filters on the same field for "between". ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich clickable table — do NOT repeat the rows as a markdown table or list; reply with a one-line summary and any insight instead.',
    inputSchema: z.object({
      filters: z
        .array(
          z.object({
            field: z
              .enum(CASE_FILTER_FIELDS)
              .describe(
                'Field to filter on. idPublic: numeric case ID — digits only, strip "AR-" and dashes from formatted IDs (use IsEqualTo). ' +
                  'applicationIdPublic: numeric application ID — digits of APP-… (IsEqualTo). ' +
                  'firstName/lastName: applicant name (Contains). status: case status (In + array of status values). ' +
                  'serviceAreaId/assistanceTypeId/submittedLanguageId/declinedReasonId: GUIDs (In + array). ' +
                  'assignedTo: numeric staff user IDs (In + array). county: county names (In + array). ' +
                  'zipCode: zip codes (ContainsAny + array). payee: payee name (Contains). ' +
                  'amountRequested/amountApproved: integer cents (range operations). ' +
                  'createdAt/updatedAt: UTC ISO dates (range operations). ' +
                  'totalHouseholdMembers/arsInApplication: integers (range operations). ' +
                  'funds/fundingSources: fund / funding-source names (ContainsAny + array). pathwayOfHope: "Yes" or "No" (IsEqualTo).'
              ),
            operation: z
              .enum(CASE_FILTER_OPERATIONS)
              .describe(
                'Comparison operation. In = match any of an array of values; ContainsAny = list-field overlap; ' +
                  'Contains = substring match on text; IsGreaterThanOrEqualTo / IsLessThanOrEqualTo = range bounds for numbers and dates.'
              ),
            value: casesFilterValueSchema,
          })
        )
        .optional()
        .describe('Filter conditions, combined with AND. Omit for the newest cases in the user’s service areas.'),
      includeDraft: z.boolean().optional().describe('Include Draft-status cases (excluded by default).'),
      includeAbandoned: z.boolean().optional().describe('Include Abandoned cases (excluded by default).'),
      includeUserDeleted: z.boolean().optional().describe('Include applicant-deleted cases (excluded by default).'),
      page: z.number().int().min(1).optional().describe('1-based page number. Default 1.'),
      pageSize: z
        .number()
        .int()
        .min(1)
        .max(25)
        .optional()
        .describe('Rows per page, max 25. Default 15. Use 1 when only the total count is needed.'),
      sortBy: z.enum(CASE_SORT_COLUMNS).optional().describe('Sort column. Default createdAt.'),
      sortOrder: z.enum(['asc', 'desc']).optional().describe('Sort direction. Default desc (newest first).'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const { query, add } = filterQuery({
        page: input.page ?? 1,
        pageSize: Math.min(input.pageSize ?? 15, 25),
        sort: input.sortBy ?? 'createdAt',
        order: input.sortOrder ?? 'desc',
      });
      (input.filters ?? []).forEach((f: any) => {
        add(f.field, Array.isArray(f.value) ? f.value.join(',') : f.value, f.operation, f.field);
      });
      if (input.includeDraft) query.includeDraft = true;
      if (input.includeAbandoned) query.includeAbandoned = true;
      if (input.includeUserDeleted) query.includeUserDeleted = true;
      // Same endpoint that powers the default Applications queue (getAll in
      // AssistanceRequestStore) — the advance-report endpoint uses a heavier
      // report view that has drifted from the DB schema in some environments.
      return { path: '/assistance-requests', query };
    },
    selectResult: (raw) => {
      const rows: any[] = raw?.result ?? raw?.data ?? (Array.isArray(raw) ? raw : []);
      return {
        count: raw?.count ?? rows.length,
        cases: rows.map((r) => {
          const row: Record<string, any> = { ...casesPick(r, CASE_ROW_FIELDS), ...casesPickNonEmpty(r, CASE_ROW_EXTRAS) };
          // Match the app's display convention (see arConverter.assignedTo): ids <= 0 mean unassigned
          if (typeof row.assignedTo === 'number' && row.assignedTo <= 0) row.assignedTo = 'Unassigned';
          return row;
        }),
      };
    },
  },
  {
    name: 'get_case',
    toolset: 'core',
    description:
      'Fetch the full detail of a single assistance request (case) by its GUID id — the id field returned by search_cases. ' +
      'If you only have a formatted public number (AR-…), run search_cases with an idPublic filter first to obtain the GUID. ' +
      'Returns status, amounts in integer cents, applicant summary (name, contact, address), assistance type, service area, ' +
      'assignment (assignedTo user id + comment), payee/payment info, decline reason, distribution/eligibility dates, and created/submitted/approved/updated timestamps. ' +
      'Use for questions about one specific case; use search_cases for lists or counts.',
    inputSchema: z.object({
      caseId: z.uuid().describe('GUID of the assistance request (the id field from search_cases results, not the AR-… number).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/assistance-requests/${input.caseId}` }),
    selectResult: (raw) => {
      if (!raw || typeof raw !== 'object') return raw;
      const { formkitForm, programForm, applicant, serviceArea, assistanceType, proofOfPaymentId, ...rest } = raw;
      return {
        ...rest,
        applicant: casesTrimApplicant(applicant),
        serviceArea: casesTrimServiceArea(serviceArea),
        assistanceType: casesPickNonEmpty(assistanceType, ['id', 'name', 'description', 'category', 'isFundingRequired']),
      };
    },
  },
  {
    name: 'get_application',
    toolset: 'core',
    description:
      'Fetch an application by its GUID id. An application is the form an applicant submits; it groups one or more assistance requests (cases). ' +
      'Get the GUID from search_cases rows (applicationId) or from get_case. If you only have an APP-… number, run search_cases with an applicationIdPublic filter first. ' +
      'Returns application status, applicant summary, the member assistance-request ids / amounts / statuses, service area, ' +
      'submitted date and language, and action flags. Use get_case for the detail of an individual case within the application.',
    inputSchema: z.object({
      applicationId: z.uuid().describe('GUID of the application (applicationId on search_cases rows, not the APP-… number).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applications/${input.applicationId}` }),
    selectResult: (raw) => {
      if (!raw || typeof raw !== 'object') return raw;
      const { formkitForm, applicant, serviceArea, ...rest } = raw;
      return {
        ...rest,
        applicant: casesTrimApplicant(applicant),
        serviceArea: casesTrimServiceArea(serviceArea),
      };
    },
  },
  {
    name: 'list_application_documents',
    toolset: 'core',
    description:
      'List the supporting documents uploaded to an application: title, file name, content type, upload/update dates, and who uploaded each. ' +
      'Metadata only — this tool cannot download, open, or read file contents. ' +
      'Use when the admin asks what documents an applicant has provided (e.g. proof of income, ID, lease). ' +
      'Takes the application GUID (applicationId from search_cases rows or get_case), not a case GUID.',
    inputSchema: z.object({
      applicationId: z.uuid().describe('GUID of the application whose documents to list.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applications/${input.applicationId}/documents` }),
    selectResult: (raw) => {
      const docs: any[] = Array.isArray(raw) ? raw : (raw?.result ?? raw?.data ?? []);
      return docs.map((d) => ({
        ...casesPick(d, ['id', 'title', 'fileName', 'contentType', 'createdAt', 'updatedAt']),
        uploadedBy: userDisplayName(d?.user),
      }));
    },
  },
  {
    name: 'assign_case',
    toolset: 'core',
    description:
      'Assign an assistance request (case) to a staff member, or unassign it. This MODIFIES data and always requires the admin to confirm ' +
      'via an in-chat confirmation card before it runs — describe exactly which case and who it will be assigned to before calling. ' +
      'assignedTo is the numeric staff role id (the same number that appears in the assignedTo field of search_cases rows / get_case); ' +
      'pass null to unassign. To resolve a staff NAME to this id, use the staff toolset — call open_toolset(\'staff\'), then ' +
      'list_staff_users or list_service_area_staff (each row\'s roleId is the value to pass here), or get_current_user for "assign to me" (its myRoleId). ' +
      'Use search_cases / get_case first to resolve the case GUID and verify it is the right case. Returns a success message.',
    inputSchema: z.object({
      caseId: z.uuid().describe('GUID of the assistance request to assign (the id field from search_cases / get_case).'),
      assignedTo: z
        .number()
        .int()
        .nullable()
        .describe(
          'Numeric staff ROLE id to assign the case to (the roleId field from list_staff_users / list_service_area_staff, or myRoleId from get_current_user — NOT the userId), or null to unassign the case.'
        ),
      comment: z.string().optional().describe('Optional note stored with the assignment and visible in the case history.'),
    }),
    method: 'PUT',
    buildRequest: (input) => ({
      path: `/assistance-requests/${input.caseId}/assign`,
      body: { assignedTo: input.assignedTo, comment: input.comment ?? null },
    }),
    requiredPermission: 'SAHelpCaseManagement',
    requiresConfirmation: true,
  },
];

// ---------------------------------------------------------------------------
// Dashboard + scheduler domain tools
// Endpoints: DashboardController / SchedulerController.
// ---------------------------------------------------------------------------

const dashboardSchedulerTools: AgentToolDef[] = [
  {
    name: 'get_dashboard_stats',
    toolset: 'core',
    description:
      'Get assistance-request dashboard statistics for one or more service areas over a date range. ' +
      "Variants: 'statuses' returns total request counts grouped by status (e.g. Submitted, Accepted, Approved, Denied, Closed) — use for questions like \"how many pending/approved cases\"; " +
      "'statuses_by_date' returns the same counts broken out per day — use for trends over time; " +
      "'by_county' returns approved/closed request counts grouped by county — use for geographic breakdowns. " +
      'If no dates are given the API defaults to the last 7 days. Returns arrays of { status, totalRequests } (with a date field for the by-date variant) or county counts.',
    inputSchema: z.object({
      variant: z
        .enum(['statuses', 'statuses_by_date', 'by_county'])
        .describe(
          "Which statistic to fetch: 'statuses' = totals by status, 'statuses_by_date' = daily counts by status, 'by_county' = approved/closed counts by county"
        ),
      serviceAreaIds: z
        .array(z.string().describe('Service area GUID'))
        .min(1)
        .describe(
          "GUIDs of the service areas to include. Use the service areas the user has access to; ask the user or use their default service area if ambiguous"
        ),
      startDate: z
        .string()
        .optional()
        .describe(
          'Range start as an ISO 8601 UTC timestamp (e.g. 2026-06-01T04:00:00.000Z). Omit for the API default of 7 days ago. When the user names a calendar date, anchor it to midnight in their timezone'
        ),
      endDate: z
        .string()
        .optional()
        .describe('Range end as an ISO 8601 UTC timestamp. Omit for the API default of now'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const paths = {
        statuses: '/dashboard/counts/statuses',
        statuses_by_date: '/dashboard/counts/statuses/date',
        by_county: '/dashboard/counts/county',
      } as const;
      return {
        path: paths[input.variant as keyof typeof paths],
        query: {
          serviceAreaIds: input.serviceAreaIds,
          ...(input.startDate ? { startDate: input.startDate } : {}),
          ...(input.endDate ? { endDate: input.endDate } : {}),
        },
      };
    },
    selectResult: (raw) => {
      // by_county payload includes a map-center coordinate the model never needs
      if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'counts' in raw) {
        return { counts: (raw as { counts: unknown }).counts };
      }
      return raw;
    },
  },
  {
    name: 'list_appointments',
    toolset: 'core',
    description:
      'Search and list scheduler appointments (applicant meetings with caseworkers). Supports filtering by applicant name/email, public applicant or application ID, appointment status, staff role, service area, and appointment start-time range, with paging and sorting. ' +
      'Use this to answer questions like "what appointments are scheduled this week", "does applicant 12345 have an upcoming appointment", or "show no-shows for last month". ' +
      'Returns a paged list of appointments with applicant name, appointment times, status, meeting type, and who the meeting is with, plus the total match count. ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich table — do NOT repeat the rows as a markdown table or list, and do NOT claim "the list above" unless you actually called this tool this turn. Reply with a one-line summary and any insight.',
    inputSchema: z.object({
      firstName: z.string().optional().describe('Filter: applicant first name contains this text'),
      lastName: z.string().optional().describe('Filter: applicant last name contains this text'),
      emailAddress: z.string().optional().describe('Filter: applicant email address contains this text'),
      applicantIdPublic: z
        .number()
        .int()
        .optional()
        .describe('Filter: exact public Applicant ID (the numeric ID staff see in the UI, not a GUID)'),
      applicationIdPublic: z
        .number()
        .int()
        .optional()
        .describe('Filter: exact public Application ID (numeric, not a GUID)'),
      statuses: z
        .array(z.enum(['Requested', 'Scheduled', 'CancelledByApplicant', 'CancelledByCaseworker', 'Complete', 'NoShow']))
        .optional()
        .describe('Filter: only appointments in these statuses'),
      roleIds: z
        .array(z.number().int())
        .optional()
        .describe('Filter: integer staff role IDs the appointment is with ("Meeting With"); matches the roleID field on appointment rows'),
      serviceAreaIds: z
        .array(z.string())
        .optional()
        .describe('Filter: service area GUIDs to restrict results to'),
      startTimeFrom: z
        .string()
        .optional()
        .describe('Filter: appointments starting at/after this ISO 8601 UTC timestamp'),
      startTimeTo: z
        .string()
        .optional()
        .describe('Filter: appointments starting at/before this ISO 8601 UTC timestamp'),
      page: z.number().int().min(1).optional().describe('Page number, 1-based. Default 1'),
      pageSize: z.number().int().min(1).max(100).optional().describe('Rows per page. Default 20; keep small'),
      sort: z
        .enum(['startTime', 'endTime', 'firstName', 'emailAddress', 'status', 'roleID', 'applicantIdPublic', 'applicationIdPublic'])
        .optional()
        .describe("Column to sort by. Default 'startTime'"),
      order: z.enum(['asc', 'desc']).optional().describe("Sort direction. Default 'asc'"),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const { query, add } = filterQuery({
        page: input.page ?? 1,
        pageSize: input.pageSize ?? 20,
        sort: input.sort ?? 'startTime',
        order: input.order ?? 'asc',
      });
      if (input.applicantIdPublic != null) add('applicantIdPublic', input.applicantIdPublic, 'IsEqualTo', 'Applicant ID');
      if (input.applicationIdPublic != null) add('applicationIdPublic', input.applicationIdPublic, 'IsEqualTo', 'Application ID');
      if (input.firstName) add('firstName', input.firstName, 'Contains', 'First Name');
      if (input.lastName) add('lastName', input.lastName, 'Contains', 'Last Name');
      if (input.emailAddress) add('emailAddress', input.emailAddress, 'Contains', 'Email Address');
      if (input.statuses?.length) add('status', input.statuses.join(','), 'In', 'Status');
      if (input.roleIds?.length) add('roleID', input.roleIds.join(','), 'In', 'Meeting With');
      if (input.serviceAreaIds?.length) add('serviceAreaIds', input.serviceAreaIds.join(','), 'In', 'Service Area');
      if (input.startTimeFrom) add('startTime', input.startTimeFrom, 'IsGreaterThanOrEqualTo', 'Appointment after');
      if (input.startTimeTo) add('startTime', input.startTimeTo, 'IsLessThanOrEqualTo', 'Appointment before');
      return { path: '/scheduler/appointments', query };
    },
    selectResult: (raw) => {
      // PagedResult payload serializes as { result: Appointment[], count, pages }
      const rows: any[] = Array.isArray(raw) ? raw : (raw?.result ?? []);
      const count: number = Array.isArray(raw) ? rows.length : (raw?.count ?? rows.length);
      return {
        count,
        appointments: rows.map((a) => ({
          id: a.id,
          applicantPublicID: a.applicantPublicID,
          applicationPublicID: a.applicationPublicID,
          firstName: a.firstName,
          lastName: a.lastName,
          emailAddress: a.emailAddress,
          startTime: a.startTime,
          endTime: a.endTime,
          status: a.status,
          roleID: a.roleID,
          userFullName: a.userFullName,
          serviceAreaID: a.serviceAreaID,
          isVirtual: a.isVirtual,
          isPhone: a.isPhone,
          isInPerson: a.isInPerson,
          inPersonAddress: a.inPersonAddress,
        })),
      };
    },
  },
  {
    name: 'list_available_slots',
    toolset: 'core',
    description:
      'Get open appointment time slots for scheduling or rescheduling an applicant meeting with a staff role. ' +
      'Requires the staff role to meet with, a time window to search, meeting length, and the meeting type (virtual, phone, or in-person; in-person also needs the location address). ' +
      'Use before proposing or booking an appointment time. Returns available slots as { startTime, endTime } pairs with which meeting types each slot supports.',
    inputSchema: z.object({
      roleId: z
        .number()
        .int()
        .describe('Integer staff role ID to meet with (same value as the roleID field on appointments)'),
      startTime: z.string().describe('Search window start, ISO 8601 UTC timestamp (e.g. 2026-07-06T13:00:00.000Z)'),
      endTime: z.string().describe('Search window end, ISO 8601 UTC timestamp. Keep the window short (a few days) to limit results'),
      meetingLength: z
        .number()
        .int()
        .optional()
        .describe('Meeting length in minutes. Default 15'),
      meetingType: z
        .enum(['virtual', 'phone', 'inPerson'])
        .describe('The meeting type the slot must support'),
      inPersonLocation: z
        .string()
        .optional()
        .describe("In-person meeting address; required when meetingType is 'inPerson' (get options from the appointment or scheduler available-locations in the UI)"),
      appointmentId: z
        .string()
        .optional()
        .describe('Existing appointment GUID to exclude from conflict checks when rescheduling that appointment'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: '/scheduler/available-slots',
      query: {
        roleId: input.roleId,
        moduleId: 9, // SAHelp module
        startTime: input.startTime,
        endTime: input.endTime,
        meetingLength: input.meetingLength ?? 15,
        [input.meetingType]: true,
        ...(input.meetingType === 'inPerson' && input.inPersonLocation ? { inPersonLocation: input.inPersonLocation } : {}),
        ...(input.appointmentId ? { apptId: input.appointmentId } : {}),
      },
    }),
    selectResult: (raw) => {
      const slots: any[] = Array.isArray(raw) ? raw : [];
      const MAX = 100;
      return {
        count: slots.length,
        truncated: slots.length > MAX,
        slots: slots.slice(0, MAX).map((s) => ({
          startTime: s.startTime,
          endTime: s.endTime,
          virtualEnabled: s.virtualEnabled,
          phoneEnabled: s.phoneEnabled,
          inPersonEnabled: s.inPersonEnabled,
          inPersonAddress: s.inPersonAddress,
        })),
      };
    },
  },
];

// ---------------------------------------------------------------------------
// Funds + configuration domain tools
// Endpoints: FundsController / AssistanceTypesController / ServiceAreasController.
// ---------------------------------------------------------------------------

// Fund/ledger amounts are stored in cents
const centsToUsd = (v: unknown) => (typeof v === 'number' ? v / 100 : 0);

const pagedParams = (input: { page?: number; pageSize?: number }) => ({
  page: input.page ?? 1,
  pageSize: Math.min(input.pageSize ?? 20, 50),
});

const fundsConfigTools: AgentToolDef[] = [
  {
    name: 'list_funds',
    toolset: 'core',
    description:
      'List the funds (pools of money drawn from a funding source and allocated to one or more service areas) visible to the current admin, with their balances. Use this to answer questions about fund balances or activity, to find a fund id by name before calling get_fund_ledger_snapshot, or to see which funds cover a funding source or service area. Returns a paged result { count, pages, funds[] } where each fund has id, name, active, fundingSourceId/fundingSourceName, serviceAreaNames, and balance figures already converted to US dollars: balanceAllocatedUsd (current allocation), balancePendingUsd + balanceApprovedUsd (encumbered/pending payments), balanceFundedUsd (issued payments), balanceAvailableUsd (unspent balance = allocation minus pending, approved and issued), currentBalanceUsd (allocation minus issued payments). Results are automatically scoped to the user’s licensed location.',
    inputSchema: z.object({
      name: z
        .string()
        .optional()
        .describe('Case-insensitive partial match on the fund name, e.g. "utility" matches "Utility Assistance Fund".'),
      active: z
        .boolean()
        .optional()
        .describe('Filter to active (true) or inactive (false) funds. Omit to include both.'),
      fundingSourceId: z
        .uuid()
        .optional()
        .describe('Only funds drawing from this funding source (GUID). Get ids from a funding-source or fund listing.'),
      serviceAreaId: z
        .uuid()
        .optional()
        .describe('Only funds attached to this service area (GUID). Resolve names to ids with list_service_areas first.'),
      page: z.number().int().min(1).optional().describe('1-based page number. Defaults to 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Defaults to 20.'),
      sort: z
        .enum([
          'name',
          'fundingSourceName',
          'balanceAllocated',
          'balancePending',
          'balanceFunded',
          'balanceAvailable',
          'currentBalance',
          'createdAt',
        ])
        .optional()
        .describe('Column to sort by. Balance columns sort by raw amount.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction, used with sort. Defaults to asc.'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const { query, add } = filterQuery(pagedParams(input));
      if (input.sort) {
        query.sort = input.sort;
        query.order = input.order ?? 'asc';
      }
      if (input.name) add('name', input.name, 'Contains');
      if (typeof input.active === 'boolean') add('active', String(input.active), 'IsEqualTo');
      if (input.fundingSourceId) add('fundingSourceID', input.fundingSourceId, 'In');
      if (input.serviceAreaId) add('serviceAreaIds', input.serviceAreaId, 'In');
      return { path: '/funds', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      funds: (raw?.result ?? []).map((f: any) => ({
        id: f.id,
        name: f.name,
        active: f.active,
        fundingSourceId: f.fundingSourceID ?? f.fundingSourceId,
        fundingSourceName: f.fundingSourceName,
        serviceAreaNames: f.serviceAreaNames ?? [],
        balanceAllocatedUsd: centsToUsd(f.balanceAllocated),
        balancePendingUsd: centsToUsd(f.balancePending),
        balanceApprovedUsd: centsToUsd(f.balanceApproved),
        balanceFundedUsd: centsToUsd(f.balanceFunded),
        balanceAvailableUsd: centsToUsd(f.balanceAvailable),
        currentBalanceUsd: centsToUsd((f.balanceAllocated ?? 0) - (f.balanceFunded ?? 0)),
        createdAt: f.createdAt,
      })),
    }),
  },
  {
    name: 'get_fund_ledger_snapshot',
    toolset: 'core',
    description:
      'Get the current financial snapshot of a single fund’s ledger. Use this when the user asks about one specific fund’s balance breakdown (find the fund id with list_funds first). Returns amounts in US dollars: initialAllocationUsd (first allocation), allocationAdjustmentsUsd (later increases/decreases), currentAllocationUsd (initial + adjustments), issuedPaymentsUsd (money paid out), pendingPaymentsUsd (payments pending or approved but not issued), currentBalanceUsd (current allocation minus issued payments), and unspentBalanceUsd (current allocation minus pending, approved and issued — what is left to commit). ' +
      'IMPORTANT: the chat UI automatically renders this result as a formatted stat card — do NOT repeat the amounts as a list or table; reply with a one-line takeaway instead.',
    inputSchema: z.object({
      fundId: z.uuid().describe('GUID of the fund. Resolve fund names to ids with list_funds.'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => ({ path: `/funds/${input.fundId}/ledger/snap-shot` }),
    selectResult: (raw) => ({
      initialAllocationUsd: centsToUsd(raw?.initialAllocation),
      allocationAdjustmentsUsd: centsToUsd(raw?.allocationAdjustments),
      currentAllocationUsd: centsToUsd(raw?.currentAllocation),
      issuedPaymentsUsd: centsToUsd(raw?.issuedPayments),
      pendingPaymentsUsd: centsToUsd(raw?.pendingPayments),
      currentBalanceUsd: centsToUsd(raw?.currentBalance),
      unspentBalanceUsd: centsToUsd(raw?.unspentBalance),
    }),
  },
  {
    name: 'list_assistance_types',
    toolset: 'core',
    description:
      'List the assistance types (e.g. rent, utilities, food) enabled for the current admin’s licensed location. Use this to resolve an assistance type name to its id, or to enumerate what kinds of help the location offers, before cross-referencing funds or cases. Takes no parameters and returns a short array of { id, name }.',
    inputSchema: z.object({}),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: () => ({ path: '/assistance-types' }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((t: any) => ({ id: t.id, name: t.name })),
  },
  {
    name: 'list_service_areas',
    toolset: 'core',
    description:
      'List the service areas (geographic coverage areas defined by ZIP codes) for the current admin’s licensed location. Use this first whenever another tool needs a service area id — it resolves service area names or ZIP codes to ids. Also answers "which service area covers ZIP x" and "what funds / assistance types are available in area y". Returns a paged result { count, pages, serviceAreas[] } where each row has id, name, active, createdAt, zipCodes (comma-separated, truncated when long), funds [{ id, name }] and nonFundedAssistanceTypes [{ id, name }] attached to the area.',
    inputSchema: z.object({
      name: z
        .string()
        .optional()
        .describe('Case-insensitive partial match on the service area name.'),
      zipCode: z
        .string()
        .optional()
        .describe('Find service areas covering a ZIP code, e.g. "78205". Multiple ZIPs may be comma-separated; matches areas covering any of them.'),
      active: z
        .boolean()
        .optional()
        .describe('Filter to active (true) or inactive (false) service areas. Omit to include both.'),
      page: z.number().int().min(1).optional().describe('1-based page number. Defaults to 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Defaults to 20.'),
      sort: z.enum(['name', 'createdAt']).optional().describe('Column to sort by.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction, used with sort. Defaults to asc.'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const { query, add } = filterQuery(pagedParams(input));
      if (input.sort) {
        query.sort = input.sort;
        query.order = input.order ?? 'asc';
      }
      if (input.name) add('name', input.name, 'Contains');
      if (input.zipCode) add('zipCodes', input.zipCode, 'ContainsAny');
      if (typeof input.active === 'boolean') add('active', String(input.active), 'IsEqualTo');
      return { path: '/service-areas', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      serviceAreas: (raw?.result ?? []).map((s: any) => ({
        id: s.id,
        name: s.name,
        active: s.active,
        createdAt: s.createdAt,
        zipCodes:
          typeof s.zipCodes === 'string' && s.zipCodes.length > 300
            ? `${s.zipCodes.slice(0, 300)}… (truncated)`
            : s.zipCodes,
        funds: (s.funds ?? []).map((f: any) => ({ id: f.id, name: f.name })),
        nonFundedAssistanceTypes: (s.nonFundedAssistanceTypes ?? []).map((t: any) => ({ id: t.id, name: t.name })),
      })),
    }),
  },
];

// ---------------------------------------------------------------------------
// Staff (users/roles) domain tools
// Endpoints: legacy UsersController (api/v1/legacy/users), ServiceAreasController
// (api/v1/service-areas/roles/*), AdminController (api/v1/admin/user-profile).
// Key id semantics: a staff person has a numeric userId AND one numeric roleId per
// role/appointment. Case assignment (assignedTo on cases, assign_case input) uses
// the ROLE id, not the user id.
// ---------------------------------------------------------------------------

// Mirrors TsammPermission (composables/Enums.ts) — the legacy API serializes enum values as numbers
const TSAMM_PERMISSION_NAMES: Record<number, string> = {
  0: 'DPISystemAdmin',
  1: 'SAHelpSupport',
  2: 'SAHelpSpiritualNeedsCounselor',
  3: 'SAHelpPaymentProcessing',
  4: 'SAHelpCaseManagement',
  5: 'SAHelpCaseManagementDirector',
  262: 'CanManageSAHelp',
  553: 'SAHelpOversight',
};

const tsammPermissionName = (v: unknown) =>
  typeof v === 'number' ? (TSAMM_PERMISSION_NAMES[v] ?? String(v)) : String(v);

// LegacyUserRoleProfile rows are heavy (blob ids, snapshot ids, timezone, …) — keep only
// what the model needs to pick a person and wire ids into other tools.
const trimStaffRow = (u: any) => ({
  roleId: u.roleId,
  userId: u.userId,
  firstName: u.userFirstName,
  lastName: u.userLastName,
  displayName: u.rankedUserName || [u.userFirstName, u.userLastName].filter(Boolean).join(' '),
  position: u.apptDesc,
  locationId: u.locationId,
  locationName: u.locationName,
});

const staffTools: AgentToolDef[] = [
  {
    name: 'get_current_user',
    toolset: 'staff',
    api: 'legacy',
    description:
      'Get the currently logged-in staff user: identity (numeric userId, roleId, adminUserId, name, email), their location ' +
      '(locationId/locationName, licensedLocationId), their TSAMM permission names (e.g. CanManageSAHelp, SAHelpCaseManagement), ' +
      'and their per-service-area role permissions (serviceAreaId + permission). ' +
      'Use this FIRST for any "my …" question: myRoleId is the value to match against the assignedTo field of cases ' +
      '("cases assigned to me" = search_cases with an assignedTo filter on myRoleId), and the service-area permission list tells you ' +
      'which service areas the user works in (useful defaults for get_dashboard_stats and search filters). Takes no parameters.',
    inputSchema: z.object({}),
    method: 'GET',
    buildRequest: () => ({ path: '/legacy/users/me' }),
    selectResult: (raw) =>
      raw && {
        userId: raw.profile?.userId,
        adminUserId: raw.adminUserId ?? null,
        // roleId is what case assignment uses: cases assigned to this user have assignedTo === myRoleId
        myRoleId: raw.profile?.roleId,
        firstName: raw.profile?.userFirstName,
        lastName: raw.profile?.userLastName,
        displayName: userDisplayName({
          rankedUserName: raw.profile?.rankedUserName,
          firstName: raw.profile?.userFirstName,
          lastName: raw.profile?.userLastName,
        }),
        email: raw.profile?.userEmail,
        position: raw.profile?.apptDesc,
        locationId: raw.profile?.locationId,
        locationName: raw.profile?.locationName,
        licensedLocationId: raw.licensedLocationId,
        // TsammPermission enum values arrive as numbers; map to names the other tools use
        tsammPermissions: (raw.tsammPermission ?? []).map(tsammPermissionName),
        serviceAreaPermissions: (raw.sahelpPermissions ?? []).map((p: any) => ({
          serviceAreaId: p.serviceAreaId,
          permission: tsammPermissionName(p.permissionId),
          roleId: p.legacyRoleId,
        })),
      },
  },
  {
    name: 'list_staff_users',
    toolset: 'staff',
    description:
      'List all staff members (caseworkers/roles) across every service area the current user can access. ' +
      'This is THE tool for resolving a staff NAME to the ids other tools need: each row has roleId — the value to pass to ' +
      'assign_case (assignedTo) and to use in search_cases assignedTo filters — plus userId, first/last/display name, position, ' +
      'and location. Also use it in reverse, to render an assignedTo roleId from a case as a person\'s name. ' +
      'A person appears once per role they hold, so the same name can repeat with different roleIds; disambiguate by position/location ' +
      'or ask the admin. Returns the full list sorted by last name (no paging). ' +
      'For staff of one specific service area use list_service_area_staff instead. ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich table — do NOT repeat the rows as a markdown table or list, and do NOT claim "the list above" unless you actually called this tool this turn. Reply with a one-line summary and any insight.',
    inputSchema: z.object({
      includePaymentProcessing: z
        .boolean()
        .optional()
        .describe('true = also include payment-processing-only roles, which the API hides by default. Omit for the default (hidden).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: '/service-areas/roles/all',
      query: input.includePaymentProcessing ? { removePaymentProcessing: false } : undefined,
    }),
    selectResult: (raw) => {
      // API returns null (not []) when the user has no accessible service areas
      const rows: any[] = Array.isArray(raw) ? raw : [];
      return { count: rows.length, staff: rows.map(trimStaffRow) };
    },
  },
  {
    name: 'list_service_area_staff',
    toolset: 'staff',
    description:
      'List the staff members (roles) assigned to ONE service area, plus location managers with the CanManageSAHelp permission. ' +
      'Each row has roleId (the value assign_case takes as assignedTo and that search_cases assignedTo filters match), userId, ' +
      'first/last/display name, position, and location — use it to resolve staff names to ids scoped to a service area, or to answer ' +
      '"who works in service area X". Resolve the service area name to its GUID with list_service_areas first. ' +
      'The same person can appear once per role they hold. Returns the full list sorted by last name (no paging). ' +
      'Set onlySchedulable=true to keep only staff with an availability schedule (the ones whose roleId works with list_available_slots). ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich table — do NOT repeat the rows as a markdown table or list, and do NOT claim "the list above" unless you actually called this tool this turn. Reply with a one-line summary and any insight.',
    inputSchema: z.object({
      serviceAreaId: z.uuid().describe('Service area GUID. Resolve names or ZIP codes to ids with list_service_areas first.'),
      onlySchedulable: z
        .boolean()
        .optional()
        .describe('true = only staff whose role has an availability schedule, i.e. can be booked via list_available_slots / appointments.'),
      excludePaymentProcessing: z
        .boolean()
        .optional()
        .describe('true = hide payment-processing-only roles. Omit for the default (included).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/service-areas/${input.serviceAreaId}/roles`,
      query: {
        ...(input.onlySchedulable ? { checkAvailable: true } : {}),
        ...(input.excludePaymentProcessing ? { removePaymentProcessing: true } : {}),
      },
    }),
    selectResult: (raw) => {
      // API returns null when the service area is unknown or has no assigned roles
      const rows: any[] = Array.isArray(raw) ? raw : [];
      return { count: rows.length, staff: rows.map(trimStaffRow) };
    },
  },
  {
    name: 'get_staff_user',
    toolset: 'staff',
    description:
      'Look up one staff member\'s profile by their numeric USER id: name, email, position, roleId, and location. ' +
      'IMPORTANT id semantics: this takes the userId (userId field on list_staff_users / list_service_area_staff rows), ' +
      'NOT the roleId that appears in a case\'s assignedTo field — to turn an assignedTo value into a name, use list_staff_users ' +
      'and match roleId instead. When the user holds several roles this returns only one of them, so prefer the list tools ' +
      'when role/service-area context matters. Fails with an error when no user has that id.',
    inputSchema: z.object({
      userId: z.number().int().describe('Numeric staff user id (the userId field from staff list rows or get_current_user — not a roleId, not a GUID).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/admin/user-profile/${input.userId}` }),
    selectResult: (raw) =>
      raw && {
        ...trimStaffRow(raw),
        email: raw.userEmail,
        licensedLocationId: raw.licensedLocationId,
        userStatus: raw.userStatus,
      },
  },
];

// ---------------------------------------------------------------------------
// Application timeline domain tools
// Endpoints: ApplicationsController (api/v1/applications/{id}/…) in the .NET
// Admin API. All six are reads gated only by the controller's class-level
// 5-permission SAHelp set, so requiredPermission is omitted (same convention
// as get_application / list_application_documents).
// ---------------------------------------------------------------------------

const TIMELINE_MAX_PAGE_SIZE = 25;

const timelineApplicationIdSchema = () =>
  z
    .uuid()
    .describe(
      'Application GUID — the applicationId field on search_cases rows, or the id from get_application / get_case. NOT the APP-… number; if you only have that, run search_cases with an applicationIdPublic filter first.'
    );

const timelinePageSchema = () => z.number().int().min(1).optional().describe('1-based page number. Default 1.');

const timelinePageSizeSchema = (what: string, defaultSize: number) =>
  z
    .number()
    .int()
    .min(1)
    .max(TIMELINE_MAX_PAGE_SIZE)
    .optional()
    .describe(`${what} per page, max ${TIMELINE_MAX_PAGE_SIZE}. Default ${defaultSize}.`);

const timelineUser = (user: any) =>
  user ? user.rankedUserName || [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined : undefined;

const timelineTruncate = (text: unknown, max = 500) =>
  typeof text === 'string' && text.length > max ? `${text.slice(0, max)}… [truncated]` : text;

// Email bodies are stored as HTML; strip tags before truncating so the snippet is readable text
const timelineEmailBody = (body: unknown) =>
  typeof body === 'string'
    ? timelineTruncate(
        body
          .replace(/<style[\s\S]*?<\/style>/gi, ' ')
          .replace(/<head[\s\S]*?<\/head>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      )
    : body;

// /history and /appointment-history both return ApplicationHistoryDto rows
const timelineHistoryRow = (h: any) => ({
  id: h.id,
  category: h.category,
  change: timelineTruncate(h.change),
  by: timelineUser(h.createdBy),
  createdAt: h.createdAt,
});

const timelineTools: AgentToolDef[] = [
  {
    name: 'get_application_comments',
    toolset: 'timeline',
    description:
      'Get the staff comments (case notes) written on an APPLICATION, paged and sorted newest first. Each comment has the note text, the staff user who wrote it, and a timestamp. Use when asked what notes or remarks staff left on an application/case. Takes the application GUID (not a case or applicant GUID — for notes on an applicant record use get_applicant_comments; for the full audit trail of everything that happened use get_application_history). Returns the total comment count plus up to pageSize comments.',
    inputSchema: z.object({
      applicationId: timelineApplicationIdSchema(),
      page: timelinePageSchema(),
      pageSize: timelinePageSizeSchema('Comments', 10),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/applications/${input.applicationId}/comments`,
      query: {
        page: input.page ?? 1,
        pageSize: Math.min(input.pageSize ?? 10, TIMELINE_MAX_PAGE_SIZE),
        sort: 'createdAt',
        order: 'desc',
      },
    }),
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      comments: (raw?.result ?? []).map((c: any) => ({
        id: c.id,
        message: timelineTruncate(c.message),
        by: timelineUser(c.user),
        createdAt: c.createdAt,
      })),
    }),
  },
  {
    name: 'get_application_history',
    toolset: 'timeline',
    description:
      'Get the complete change-history/audit trail of an application: status changes, form-answer edits, assignments, approvals/declines, file uploads, consents, and other staff or system actions across the application and its assistance requests. Each entry has a category, a human-readable description of the change, who did it, and a timestamp. This is the primary tool for "what happened on this application and when" or "who changed this case". Returns the full list (not paged; entries can be numerous on old applications). For staff-written notes use get_application_comments; for scheduling events use get_application_appointment_history.',
    inputSchema: z.object({
      applicationId: timelineApplicationIdSchema(),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applications/${input.applicationId}/history` }),
    selectResult: (raw) => (Array.isArray(raw) ? raw : []).map(timelineHistoryRow),
  },
  {
    name: 'get_application_appointment_history',
    toolset: 'timeline',
    description:
      'Get the appointment/scheduling event history for an application: appointments requested, scheduled, rescheduled, cancelled, completed, or missed (no-show), including food-pantry distribution appointments when the application has a distributable assistance type. Each entry has a category, a human-readable description, who acted, and a timestamp. Use for "did this applicant have/miss an appointment" or "when was their meeting scheduled". Returns the full list (not paged). Use list_appointments to search upcoming appointments across applicants; use this tool for the scheduling story of ONE application.',
    inputSchema: z.object({
      applicationId: timelineApplicationIdSchema(),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applications/${input.applicationId}/appointment-history` }),
    selectResult: (raw) => (Array.isArray(raw) ? raw : []).map(timelineHistoryRow),
  },
  {
    name: 'get_application_emails',
    toolset: 'timeline',
    description:
      'List every email the system sent to the applicant about an application and its assistance requests (status notifications, requests for documents, approval/decline notices, etc.; one-time-passcode emails are excluded). Each email has the subject, recipient and sender addresses, created/sent timestamps, a plain-text body snippet (truncated to ~500 characters), and attachment file names. Use to answer "what was the applicant told by email" or "did they get the approval notice". Returns ALL matching emails in one response (count + emails; not paged), newest first by default.',
    inputSchema: z.object({
      applicationId: timelineApplicationIdSchema(),
      order: z
        .enum(['asc', 'desc'])
        .optional()
        .describe('Sort by send/created date: desc = newest first (default), asc = oldest first.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/applications/${input.applicationId}/emails-received`,
      // the endpoint only honors ordering on the 'created' column
      query: { sort: 'created', order: input.order ?? 'desc' },
    }),
    selectResult: (raw) => ({
      count: raw?.count ?? (raw?.result ?? []).length,
      emails: (raw?.result ?? []).map((e: any) => ({
        id: e.id,
        subject: e.subject,
        to: e.destinationEmail,
        from: e.sourceRealName || e.sourceEmail,
        createdAt: e.created,
        sentAt: e.sent,
        body: timelineEmailBody(e.body),
        attachments: (e.attachments ?? []).map((a: any) => a.fileName).filter(Boolean),
      })),
    }),
  },
  {
    name: 'get_application_sms',
    toolset: 'timeline',
    description:
      'List every SMS/text message the system sent to the applicant about an application (status updates, appointment reminders, etc.). Each message has the destination phone number, the message text (truncated to ~500 characters), and a sent timestamp. Use to answer "what texts did the applicant receive" or "were they reminded about their appointment". Returns ALL matching messages in one response (count + messages; not paged), newest first by default. For two-way in-app chat use get_application_chat_history instead.',
    inputSchema: z.object({
      applicationId: timelineApplicationIdSchema(),
      order: z
        .enum(['asc', 'desc'])
        .optional()
        .describe('Sort by sent date: desc = newest first (default), asc = oldest first.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/applications/${input.applicationId}/sms-received`,
      // the endpoint only honors ordering on the 'created' column
      query: { sort: 'created', order: input.order ?? 'desc' },
    }),
    selectResult: (raw) => ({
      count: raw?.count ?? (raw?.result ?? []).length,
      messages: (raw?.result ?? []).map((s: any) => ({
        id: s.id,
        to: s.destinationNumber,
        body: timelineTruncate(s.bodyText),
        createdAt: s.created,
      })),
    }),
  },
  {
    name: 'get_application_chat_history',
    toolset: 'timeline',
    description:
      'Get the two-way in-app chat conversation between the applicant and staff on an application, paged and in chronological order (oldest first by default; use order "desc" for the most recent messages). Each message has the sender (applicant, staff, or system), the staff member\'s name for staff messages, the message text (truncated to ~500 characters), the message type (text vs file attachment), sent/read timestamps, and a deleted flag. Use to answer "what did staff and the applicant discuss" or "did the applicant reply in chat". For one-way notification emails/texts use get_application_emails / get_application_sms. Note: fetching history MARKS the applicant\'s unread messages as READ for this admin — the conversation will no longer show as pending/unread (in list_pending_chats or the chat UI badge).',
    inputSchema: z.object({
      applicationId: timelineApplicationIdSchema(),
      page: timelinePageSchema(),
      pageSize: timelinePageSizeSchema('Messages', 20),
      order: z
        .enum(['asc', 'desc'])
        .optional()
        .describe('Chronological order: asc = oldest first (default, matches the chat UI), desc = newest first.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/applications/${input.applicationId}/chat-history`,
      query: {
        page: input.page ?? 1,
        pageSize: Math.min(input.pageSize ?? 20, TIMELINE_MAX_PAGE_SIZE),
        sort: 'createdAt',
        order: input.order ?? 'asc',
      },
    }),
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      messages: (raw?.result ?? []).map((m: any) => ({
        id: m.id,
        sender: m.isSystemMessage ? 'system' : m.adminId != null ? 'staff' : 'applicant',
        staffName: timelineUser(m.admin),
        message: timelineTruncate(m.message),
        messageType: m.messageType,
        createdAt: m.createdAt,
        readAt: m.readAt,
        deleted: m.deleted || undefined,
      })),
    }),
  },
];

// ---------------------------------------------------------------------------
// Money domain tools
// Endpoints: FundsController + FundingSourcesController (api/v1/funds,
// api/v1/funding-sources). All amounts are stored in integer cents; every
// selectResult converts to US dollars before the value reaches the model.
// DO NOT wrap GET /funding-sources/funding-source-warning/notifications —
// despite the GET verb it dispatches warning emails/notifications.
// ---------------------------------------------------------------------------

// EnumLedgerTransactionType values shared by fund and funding-source ledgers
const LEDGER_TRANSACTION_TYPES = [
  'RequestApprovalPending',
  'RequestApproved',
  'RequestDeclined',
  'RequestFunded',
  'RequestRescinded',
  'FundingSourceIncreased',
  'FundingSourceDecreased',
  'FundIncreased',
  'FundDecreased',
  'RequestAmountEdited',
  'RequestFundedReverted',
  'RequestApprovedReverted',
  'RequestApprovalPendingReverted',
  'RequestRescindedReverted',
] as const;

// EnumLedgerAction — the user-friendly action label on all-funds ledger rows
const LEDGER_ACTIONS = [
  'InitialAllocation',
  'AllocationAdjustment',
  'FundsAssigned',
  'FundsIssued',
  'FundsRescinded',
] as const;

const moneyTools: AgentToolDef[] = [
  {
    name: 'list_funding_sources',
    toolset: 'money',
    description:
      'List the funding sources (grants and general-operating awards that funds draw from) for the current admin’s licensed location, with award/balance figures. The peer of list_funds one level up the money hierarchy: funding source → funds → payments. Use to answer "what grants do we have", "how much of grant X is left/unallocated", to resolve a funding-source name to its GUID for get_funding_source / list_funds(fundingSourceId), or to find sources nearing their end date or low on money. Returns a paged result { count, pages, fundingSources[] } where each row has id, name, grantsNumber, isActive, type (Grant | GeneralOperating), startDate/endDate, serviceAreaNames, and balances converted to US dollars: balanceAwardedUsd (total awarded), balanceAllocatedUsd (allocated into funds), balancePendingUsd + balanceApprovedUsd (encumbered), balanceFundedUsd (issued payments), balanceAvailableUsd (unspent = allocated minus pending/approved/issued), currentBalanceUsd (awarded minus issued), plus balanceAvailablePercent (unspent as a percent of the award). Results are automatically scoped to the user’s licensed location. ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich table (name, type, status, awarded, available, available %) — do NOT repeat the rows as a markdown table or list, and do NOT claim "the list above" unless you actually called this tool this turn. Reply with a one-line summary and any insight.',
    inputSchema: z.object({
      name: z.string().optional().describe('Case-insensitive partial match on the funding source name, e.g. "FEMA".'),
      active: z.boolean().optional().describe('Filter to active (true) or inactive (false) funding sources. Omit for both.'),
      type: z
        .enum(['Grant', 'GeneralOperating'])
        .optional()
        .describe('Filter by funding source type: Grant (restricted award) or GeneralOperating (general funds).'),
      minAwardedCents: z
        .number()
        .int()
        .optional()
        .describe('Only sources awarded at least this amount, in integer cents ($50,000.00 = 5000000).'),
      maxAwardedCents: z.number().int().optional().describe('Only sources awarded at most this amount, in integer cents.'),
      minUnspentCents: z
        .number()
        .int()
        .optional()
        .describe('Only sources with at least this unspent balance, in integer cents.'),
      maxUnspentCents: z
        .number()
        .int()
        .optional()
        .describe('Only sources with at most this unspent balance, in integer cents. Use to find nearly-exhausted sources.'),
      minUnspentPercent: z
        .number()
        .optional()
        .describe('Only sources whose unspent balance is at least this percent of the award (0–100).'),
      maxUnspentPercent: z
        .number()
        .optional()
        .describe('Only sources whose unspent balance is at most this percent of the award (0–100), e.g. 10 for "under 10% left".'),
      endDateAfter: z
        .string()
        .optional()
        .describe('Only sources whose end date is on/after this UTC ISO timestamp, e.g. "2026-09-30T00:00:00Z".'),
      endDateBefore: z
        .string()
        .optional()
        .describe('Only sources whose end date is on/before this UTC ISO timestamp. Use to find sources expiring soon.'),
      page: z.number().int().min(1).optional().describe('1-based page number. Defaults to 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Defaults to 20.'),
      sort: z
        .enum([
          'name',
          'isActive',
          'balanceAwarded',
          'balanceFunded',
          'currentBalance',
          'balanceAvailable',
          'balanceAvailablePercent',
          'endDate',
          'createdAt',
        ])
        .optional()
        .describe('Column to sort by. Balance columns sort by raw cent amount.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction, used with sort. Defaults to asc.'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const { query, add } = filterQuery(pagedParams(input));
      if (input.sort) {
        query.sort = input.sort;
        query.order = input.order ?? 'asc';
      }
      if (input.name) add('name', input.name, 'Contains');
      if (typeof input.active === 'boolean') add('isActive', String(input.active), 'IsEqualTo');
      if (input.type) add('type', input.type, 'In');
      if (input.minAwardedCents != null) add('balanceAwarded', input.minAwardedCents, 'IsGreaterThanOrEqualTo');
      if (input.maxAwardedCents != null) add('balanceAwarded', input.maxAwardedCents, 'IsLessThanOrEqualTo');
      if (input.minUnspentCents != null) add('balanceAvailable', input.minUnspentCents, 'IsGreaterThanOrEqualTo');
      if (input.maxUnspentCents != null) add('balanceAvailable', input.maxUnspentCents, 'IsLessThanOrEqualTo');
      if (input.minUnspentPercent != null) add('balanceAvailablePercent', input.minUnspentPercent, 'IsGreaterThanOrEqualTo');
      if (input.maxUnspentPercent != null) add('balanceAvailablePercent', input.maxUnspentPercent, 'IsLessThanOrEqualTo');
      if (input.endDateAfter) add('endDate', input.endDateAfter, 'IsGreaterThanOrEqualTo');
      if (input.endDateBefore) add('endDate', input.endDateBefore, 'IsLessThanOrEqualTo');
      return { path: '/funding-sources', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      fundingSources: (raw?.result ?? []).map((s: any) => ({
        id: s.id,
        idPublic: s.idPublic,
        name: s.name,
        grantsNumber: s.grantsNumber,
        isActive: s.isActive,
        type: s.type,
        startDate: s.startDate,
        endDate: s.endDate,
        serviceAreaNames: s.serviceAreaNames ?? [],
        balanceAwardedUsd: centsToUsd(s.balanceAwarded),
        balanceAllocatedUsd: centsToUsd(s.balanceAllocated),
        balancePendingUsd: centsToUsd(s.balancePending),
        balanceApprovedUsd: centsToUsd(s.balanceApproved),
        balanceFundedUsd: centsToUsd(s.balanceFunded),
        balanceAvailableUsd: centsToUsd(s.balanceAvailable),
        currentBalanceUsd: centsToUsd(s.currentBalance),
        balanceAvailablePercent: s.balanceAvailablePercent,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
      })),
    }),
  },
  {
    name: 'get_fund',
    toolset: 'money',
    description:
      'Get one fund in full by its GUID (resolve names to ids with list_funds first). Beyond the list_funds row, this returns the fund’s instructions text, whether it was deactivated by its funding source, the parent funding source summary (id, name, type, active, grant number, start/end dates), the service areas and assistance types the fund is attached to, and the latest ledger balances in US dollars. Use when the admin asks about one specific fund’s configuration ("which assistance types does the Rent fund cover?", "what are the instructions on fund X?"). For the balance breakdown alone prefer get_fund_ledger_snapshot; for transaction history use list_fund_ledger_transactions.',
    inputSchema: z.object({
      fundId: z.uuid().describe('GUID of the fund. Resolve fund names to ids with list_funds.'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => ({ path: `/funds/${input.fundId}` }),
    selectResult: (raw) =>
      raw && {
        id: raw.id,
        name: raw.name,
        active: raw.active,
        instructions: raw.instructions,
        deactivatedByFundingSource: raw.deactivatedByFundingSource,
        createdAt: raw.createdAt,
        updatedAt: raw.updatedAt,
        fundingSource: raw.fundingSource && {
          id: raw.fundingSource.id,
          name: raw.fundingSource.name,
          grantsNumber: raw.fundingSource.grantsNumber,
          isActive: raw.fundingSource.isActive,
          type: raw.fundingSource.type,
          startDate: raw.fundingSource.startDate,
          endDate: raw.fundingSource.endDate,
        },
        serviceAreas: (raw.serviceAreas ?? []).map((sa: any) => ({ id: sa.id, name: sa.name })),
        assistanceTypes: (raw.assistanceTypes ?? []).map((at: any) => ({
          id: at.id,
          name: at.name,
          isFundingRequired: at.isFundingRequired,
        })),
        latestLedger: raw.fundsLedger && {
          balanceAllocatedUsd: centsToUsd(raw.fundsLedger.balanceAllocated),
          balancePendingUsd: centsToUsd(raw.fundsLedger.balancePending),
          balanceApprovedUsd: centsToUsd(raw.fundsLedger.balanceApproved),
          balanceFundedUsd: centsToUsd(raw.fundsLedger.balanceFunded),
          balanceAvailableUsd: centsToUsd(raw.fundsLedger.balanceAvailable),
          lastTransactionType: raw.fundsLedger.transactionType,
          lastTransactionAt: raw.fundsLedger.createdAt,
        },
      },
  },
  {
    name: 'list_fund_ledger_transactions',
    toolset: 'money',
    description:
      'List the ledger transactions of ONE fund (allocations in/out and case-payment movements), newest first by default. Use to answer "what happened to fund X’s balance", "when was fund X last topped up", or to trace a balance change over a date range (resolve the fund id with list_funds first). Each transaction row has the transaction type, the transaction amount and the running balances after it — all converted to US dollars — plus an optional comment/description and timestamp. Transaction types: FundIncreased/FundDecreased = manual allocation adjustments; RequestApprovalPending/RequestApproved/RequestFunded = money encumbered/paid on a case; RequestRescinded/RequestDeclined and *Reverted types release money back. Returns { count, pages, transactions[] }. For a cross-fund view (with applicant/case context) use list_all_fund_ledger_transactions instead. ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich table — do NOT repeat the rows as a markdown table or list, and do NOT claim "the list above" unless you actually called this tool this turn. Reply with a one-line summary and any insight.',
    inputSchema: z.object({
      fundId: z.uuid().describe('GUID of the fund whose ledger to list. Resolve fund names to ids with list_funds.'),
      transactionTypes: z
        .array(z.enum(LEDGER_TRANSACTION_TYPES))
        .optional()
        .describe('Only these transaction types, e.g. ["FundIncreased","FundDecreased"] for allocation adjustments only.'),
      createdAfter: z
        .string()
        .optional()
        .describe('Only transactions on/after this UTC ISO timestamp, e.g. "2026-06-01T00:00:00Z".'),
      createdBefore: z.string().optional().describe('Only transactions on/before this UTC ISO timestamp.'),
      page: z.number().int().min(1).optional().describe('1-based page number. Defaults to 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Defaults to 20.'),
      sort: z
        .enum(['createdAt', 'amount', 'transactionType'])
        .optional()
        .describe('Column to sort by. Defaults to createdAt.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction. Defaults to desc (newest first).'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => {
      const { query, add } = filterQuery(pagedParams(input));
      query.sort = input.sort ?? 'createdAt';
      query.order = input.order ?? 'desc';
      if (input.transactionTypes?.length) add('transactionType', input.transactionTypes.join(','), 'In');
      if (input.createdAfter) add('createdAt', input.createdAfter, 'IsGreaterThanOrEqualTo');
      if (input.createdBefore) add('createdAt', input.createdBefore, 'IsLessThanOrEqualTo');
      return { path: `/funds/${input.fundId}/ledger`, query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      transactions: (raw?.result ?? []).map((t: any) => ({
        id: t.id,
        transactionType: t.transactionType,
        amountUsd: centsToUsd(t.amount),
        balanceAllocatedUsd: centsToUsd(t.balanceAllocated),
        balancePendingUsd: centsToUsd(t.balancePending),
        balanceApprovedUsd: centsToUsd(t.balanceApproved),
        balanceFundedUsd: centsToUsd(t.balanceFunded),
        balanceAvailableUsd: centsToUsd(t.balanceAvailable),
        comment: t.comment,
        description: t.description,
        createdAt: t.createdAt,
      })),
    }),
  },
  {
    name: 'list_all_fund_ledger_transactions',
    toolset: 'money',
    description:
      'Search fund ledger transactions across ALL funds in the current admin’s licensed location — the "Fund Transactions" report. Each row carries full context: action (InitialAllocation, AllocationAdjustment, FundsAssigned = encumbered on a case, FundsIssued = paid, FundsRescinded = released), fund and funding source names, service area, and — for case-driven transactions — the applicant name, public applicant/application/case IDs, and assistance type. Use for questions like "what was spent from the FEMA grant last month", "show all payments issued this week across funds", or to trace which cases drew from a fund. Amounts are converted to US dollars. Filter money values in integer cents ($50.00 = 5000). Internal accounting rows (RequestApproved and reverted approve/fund events) are excluded server-side. Returns { count, pages, transactions[] }. For one fund’s simple balance history use list_fund_ledger_transactions.',
    inputSchema: z.object({
      actions: z
        .array(z.enum(LEDGER_ACTIONS))
        .optional()
        .describe('Only these actions, e.g. ["FundsIssued"] for issued payments only.'),
      fundIds: z
        .array(z.uuid())
        .optional()
        .describe('Only transactions on these funds (GUIDs from list_funds).'),
      fundingSourceIds: z
        .array(z.uuid())
        .optional()
        .describe('Only transactions on funds drawing from these funding sources (GUIDs from list_funding_sources).'),
      serviceAreaIds: z
        .array(z.uuid())
        .optional()
        .describe('Only transactions tied to these service areas (GUIDs from list_service_areas).'),
      assistanceTypeIds: z
        .array(z.uuid())
        .optional()
        .describe('Only case-driven transactions for these assistance types (GUIDs from list_assistance_types).'),
      applicantFirstName: z.string().optional().describe('Applicant first name, partial match (contains).'),
      applicantLastName: z.string().optional().describe('Applicant last name, partial match (contains).'),
      assistanceRequestIdPublic: z
        .number()
        .int()
        .optional()
        .describe('Exact public case ID — digits only, strip "AR-" and dashes from formatted IDs.'),
      minAmountCents: z
        .number()
        .int()
        .optional()
        .describe('Only transactions of at least this amount, in integer cents ($50.00 = 5000).'),
      maxAmountCents: z.number().int().optional().describe('Only transactions of at most this amount, in integer cents.'),
      createdAfter: z
        .string()
        .optional()
        .describe('Only transactions on/after this UTC ISO timestamp, e.g. "2026-06-01T00:00:00Z".'),
      createdBefore: z.string().optional().describe('Only transactions on/before this UTC ISO timestamp.'),
      page: z.number().int().min(1).optional().describe('1-based page number. Defaults to 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Defaults to 20.'),
      sort: z
        .enum(['createdAt', 'amount', 'action', 'fundName', 'fundingSourceName', 'serviceAreaName'])
        .optional()
        .describe('Column to sort by ("amount" sorts by the computed transaction amount). Defaults to createdAt.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction. Defaults to desc (newest first).'),
    }),
    method: 'GET',
    // Server also grants SAHelpCaseManagementDirector; registry supports one permission
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => {
      const { query, add } = filterQuery(pagedParams(input));
      query.sort = input.sort ?? 'createdAt';
      query.order = input.order ?? 'desc';
      if (input.actions?.length) add('action', input.actions.join(','), 'In', 'Action');
      if (input.fundIds?.length) add('fundId', input.fundIds.join(','), 'In', 'Fund');
      if (input.fundingSourceIds?.length) add('fundingSourceId', input.fundingSourceIds.join(','), 'In', 'Funding Source');
      if (input.serviceAreaIds?.length) add('serviceAreaId', input.serviceAreaIds.join(','), 'In', 'Service Area');
      if (input.assistanceTypeIds?.length)
        add('assistanceTypeId', input.assistanceTypeIds.join(','), 'In', 'Assistance Type');
      if (input.applicantFirstName) add('applicantFirstName', input.applicantFirstName, 'Contains');
      if (input.applicantLastName) add('applicantLastName', input.applicantLastName, 'Contains');
      if (input.assistanceRequestIdPublic != null)
        add('assistanceRequestIdPublic', input.assistanceRequestIdPublic, 'IsEqualTo');
      if (input.minAmountCents != null) add('computedAmount', input.minAmountCents, 'IsGreaterThanOrEqualTo');
      if (input.maxAmountCents != null) add('computedAmount', input.maxAmountCents, 'IsLessThanOrEqualTo');
      if (input.createdAfter) add('createdAt', input.createdAfter, 'IsGreaterThanOrEqualTo');
      if (input.createdBefore) add('createdAt', input.createdBefore, 'IsLessThanOrEqualTo');
      return { path: '/funds/ledgers', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      transactions: (raw?.result ?? []).map((t: any) => ({
        id: t.id,
        createdAt: t.createdAt,
        action: t.action,
        transactionType: t.transactionType,
        amountUsd: centsToUsd(t.computedAmount),
        pendingPaymentsUsd: centsToUsd(t.pendingApprovedTotal),
        issuedPaymentsUsd: centsToUsd(t.balanceFunded),
        unspentBalanceUsd: centsToUsd(t.balanceAvailable),
        fundId: t.fundId,
        fundName: t.fundName,
        fundingSourceId: t.fundingSourceId,
        fundingSourceName: t.fundingSourceName,
        serviceAreaName: t.serviceAreaName,
        applicantFirstName: t.applicantFirstName,
        applicantLastName: t.applicantLastName,
        applicantIdPublic: t.applicantIdPublic,
        applicationIdPublic: t.applicationIdPublic,
        assistanceRequestId: t.assistanceRequestId,
        assistanceRequestIdPublic: t.assistanceRequestIdPublic,
        assistanceType: t.assistanceTypeDescription,
        by: t.userFullName,
      })),
    }),
  },
  {
    name: 'get_funding_source',
    toolset: 'money',
    description:
      'Get one funding source in full by its GUID (resolve names to ids with list_funding_sources first). Returns name, grant number, active flag, type (Grant | GeneralOperating), start/end dates, account number, the assistance type ids the source may pay for, and the latest ledger balances in US dollars (awarded, allocated into funds, pending, approved, issued, available). Use when the admin asks about one specific funding source’s configuration or grant details. For the full amount breakdown prefer get_funding_source_ledger_snapshot; for transaction history use list_funding_source_ledger.',
    inputSchema: z.object({
      fundingSourceId: z
        .uuid()
        .describe('GUID of the funding source. Resolve names to ids with list_funding_sources.'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => ({ path: `/funding-sources/${input.fundingSourceId}` }),
    selectResult: (raw) =>
      raw && {
        id: raw.id,
        name: raw.name,
        grantsNumber: raw.grantsNumber,
        isActive: raw.isActive,
        type: raw.type,
        startDate: raw.startDate,
        endDate: raw.endDate,
        accountNumber: raw.accountNumber,
        assistanceTypeIds: raw.assistanceTypeIds ?? [],
        createdAt: raw.createdAt,
        updatedAt: raw.updatedAt,
        latestLedger: raw.ledger && {
          balanceAwardedUsd: centsToUsd(raw.ledger.balanceAwarded),
          balanceAllocatedUsd: centsToUsd(raw.ledger.balanceAllocated),
          balancePendingUsd: centsToUsd(raw.ledger.balancePending),
          balanceApprovedUsd: centsToUsd(raw.ledger.balanceApproved),
          balanceFundedUsd: centsToUsd(raw.ledger.balanceFunded),
          balanceAvailableUsd: centsToUsd(raw.ledger.balanceAvailable),
          lastTransactionType: raw.ledger.transactionType,
        },
      },
  },
  {
    name: 'get_funding_source_ledger_snapshot',
    toolset: 'money',
    description:
      'Get the current financial snapshot of a single funding source’s ledger — the authoritative answer to "how much of grant X is left". Use after resolving the funding source id with list_funding_sources. Returns amounts in US dollars: initialAwardUsd (first award), awardAdjustmentsUsd (later increases/decreases), currentAwardUsd (initial + adjustments), fundAllocationUsd (moved into funds), unallocatedUsd (award not yet placed in any fund), totalIssuedPaymentsUsd (paid out), totalPendingPaymentsUsd (pending or approved but not issued), currentBalanceOfFundsUsd (fund allocation minus issued), unspentBalanceOfFundsUsd (fund allocation minus pending, approved and issued), and unspentBalanceUsd (current award minus pending, approved and issued — the bottom line remaining). For the fund-level equivalent use get_fund_ledger_snapshot. ' +
      'IMPORTANT: the chat UI automatically renders this result as a formatted stat card — do NOT repeat the amounts as a list or table; reply with a one-line takeaway.',
    inputSchema: z.object({
      fundingSourceId: z
        .uuid()
        .describe('GUID of the funding source. Resolve names to ids with list_funding_sources.'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => ({ path: `/funding-sources/${input.fundingSourceId}/ledger/snap-shot` }),
    selectResult: (raw) => ({
      initialAwardUsd: centsToUsd(raw?.initialAward),
      awardAdjustmentsUsd: centsToUsd(raw?.awardAdjustments),
      currentAwardUsd: centsToUsd(raw?.currentAward),
      fundAllocationUsd: centsToUsd(raw?.fundAllocation),
      unallocatedUsd: centsToUsd(raw?.unallocated),
      totalIssuedPaymentsUsd: centsToUsd(raw?.totalIssuedPayments),
      totalPendingPaymentsUsd: centsToUsd(raw?.totalPendingPayments),
      currentBalanceOfFundsUsd: centsToUsd(raw?.currentBalanceOfFunds),
      unspentBalanceOfFundsUsd: centsToUsd(raw?.unspentBalanceOfFunds),
      unspentBalanceUsd: centsToUsd(raw?.unspentBalance),
    }),
  },
  {
    name: 'list_funding_source_ledger',
    toolset: 'money',
    description:
      'List the ledger transactions of ONE funding source (award increases/decreases and allocations into funds), newest first by default. Use to answer "when was grant X increased", "what allocation adjustments were made to source Y", or to trace an award’s history over a date range (resolve the id with list_funding_sources first). Each row has the transaction type (FundingSourceIncreased/FundingSourceDecreased = award adjustments; FundIncreased/FundDecreased = money moved to/from a fund), the amount and running balances in US dollars, an optional comment/description, the timestamp, and the staff member who made it. Returns { count, pages, transactions[] }. ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich table — do NOT repeat the rows as a markdown table or list, and do NOT claim "the list above" unless you actually called this tool this turn. Reply with a one-line summary and any insight.',
    inputSchema: z.object({
      fundingSourceId: z
        .uuid()
        .describe('GUID of the funding source whose ledger to list. Resolve names to ids with list_funding_sources.'),
      transactionTypes: z
        .array(z.enum(LEDGER_TRANSACTION_TYPES))
        .optional()
        .describe(
          'Only these transaction types, e.g. ["FundingSourceIncreased","FundingSourceDecreased"] for award adjustments only.'
        ),
      createdAfter: z
        .string()
        .optional()
        .describe('Only transactions on/after this UTC ISO timestamp, e.g. "2026-06-01T00:00:00Z".'),
      createdBefore: z.string().optional().describe('Only transactions on/before this UTC ISO timestamp.'),
      page: z.number().int().min(1).optional().describe('1-based page number. Defaults to 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Defaults to 20.'),
      sort: z
        .enum(['createdAt', 'amount', 'transactionType'])
        .optional()
        .describe('Column to sort by. Defaults to createdAt.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction. Defaults to desc (newest first).'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const { query, add } = filterQuery(pagedParams(input));
      query.sort = input.sort ?? 'createdAt';
      query.order = input.order ?? 'desc';
      if (input.transactionTypes?.length) add('transactionType', input.transactionTypes.join(','), 'In');
      if (input.createdAfter) add('createdAt', input.createdAfter, 'IsGreaterThanOrEqualTo');
      if (input.createdBefore) add('createdAt', input.createdBefore, 'IsLessThanOrEqualTo');
      return { path: `/funding-sources/${input.fundingSourceId}/ledger`, query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      transactions: (raw?.result ?? []).map((t: any) => ({
        id: t.id,
        transactionType: t.transactionType,
        amountUsd: centsToUsd(t.amount),
        balanceAwardedUsd: centsToUsd(t.balanceAwarded),
        balanceAllocatedUsd: centsToUsd(t.balanceAllocated),
        balancePendingUsd: centsToUsd(t.balancePending),
        balanceApprovedUsd: centsToUsd(t.balanceApproved),
        balanceFundedUsd: centsToUsd(t.balanceFunded),
        balanceAvailableUsd: centsToUsd(t.balanceAvailable),
        comment: t.comment,
        description: t.description,
        createdAt: t.createdAt,
        by: userDisplayName(t.createdBy),
      })),
    }),
  },
  {
    name: 'list_funding_source_documents',
    toolset: 'money',
    description:
      'List the supporting documents attached to a funding source (grant agreements, award letters, amendments): title, file name, content type, upload/update dates, and who uploaded each. Metadata only — this tool cannot download, open, or read file contents. Use when the admin asks what paperwork is on file for a grant or funding source. Resolve the funding source id with list_funding_sources first.',
    inputSchema: z.object({
      fundingSourceId: z
        .uuid()
        .describe('GUID of the funding source whose documents to list. Resolve names to ids with list_funding_sources.'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => ({ path: `/funding-sources/${input.fundingSourceId}/documents` }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((d: any) => ({
        id: d.id,
        title: d.title,
        fileName: d.fileName,
        contentType: d.contentType,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
        uploadedBy: userDisplayName(d.user),
      })),
  },
  {
    name: 'get_funding_source_alerts',
    toolset: 'money',
    description:
      'Get the currently-firing funding source warning alerts for the admin’s licensed location — the same low-balance / expiring-source warnings shown at login. Read-only: this checks alert state, it does NOT send any notifications. Use for "are any of our funding sources running low or about to expire?" or as a proactive money health check. Takes no parameters. Returns an array of alerts, each with the funding source (id + name), alertType, currentValue vs threshold (percent or amount depending on the alert configuration), notificationType, affected serviceAreaNames, and when the alert was last sent. An empty array means no funding source warnings are active.',
    inputSchema: z.object({}),
    method: 'GET',
    buildRequest: () => ({ path: '/funding-sources/funding-source-warning/alerts' }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((a: any) => ({
        fundingSourceId: a.fundingSourceId,
        fundingSourceName: a.fundingSourceName,
        alertType: a.alertType,
        currentValue: a.currentValue,
        threshold: a.threshold,
        notificationType: a.notificationType,
        serviceAreaNames: a.serviceAreaNames ?? [],
        lastSent: a.lastSent,
      })),
  },
];

// ---------------------------------------------------------------------------
// Case-detail domain tools
// Endpoints: ApplicationsController (api/v1/applications) and
// AssistanceRequestsController (api/v1/assistance-requests) in the .NET Admin API.
// All are reads gated only by the controllers' class-level 5-permission SAHelp
// set, so requiredPermission is omitted (matching the existing tools' convention).
// ---------------------------------------------------------------------------

const caseDetailDisplayName = (user: any) =>
  user
    ? user.rankedUserName || [user.userFirstName ?? user.firstName, user.userLastName ?? user.lastName].filter(Boolean).join(' ') || user.userName
    : undefined;

// Shared shape of duplicate-check rows (AssistanceRequestOriginalGetDto)
const caseDetailTrimDuplicateRow = (r: any) => ({
  applicationId: r.applicationId,
  assistanceRequestId: r.assistanceRequestId,
  assistanceRequestIdPublic: r.assistanceRequestIdPublic,
  status: r.status,
  statusDate: r.statusDate,
  assistanceTypeId: r.assistanceTypeId,
  assistanceTypeName: r.assistanceTypeName,
  applicantId: r.applicantId,
  applicantsPublicId: r.applicantsPublicId,
  applicantsFirstName: r.applicantsFirstName,
  applicantsLastName: r.applicantsLastName,
  applicantsDateOfBirth: r.applicantsDateOfBirth,
  applicantsAddress1: r.applicantsAddress1,
  applicantsAddress2: r.applicantsAddress2,
  city: r.city,
  state: r.state,
  zipcode: r.zipcode,
  payee: r.payee,
  accountNumber: r.accountNumber,
});

const caseDetailTools: AgentToolDef[] = [
  {
    name: 'check_application_duplicates',
    toolset: 'case-detail',
    description:
      'Run the configured duplicate-detection check for an application: compares every assistance request in the application against other cases at the licensed location. ' +
      'Use when the admin asks whether a case/application looks like a duplicate, or before recommending approval. ' +
      'Takes the APPLICATION GUID (applicationId from search_cases rows or get_case — not a case GUID and not an APP-… number). ' +
      'Returns { originalAssistanceRequests, possibleDuplicates }: the originals are the requests inside this application; each possible duplicate row adds ' +
      'originalAssistanceRequestId (which original it matched), duplicateType (ApplicantAddress = same address + assistance type, PayeeAccountNumber = same payee account number, ' +
      'or a name + date-of-birth match), isNotDuplicate (true when staff already reviewed and dismissed it, with reviewedBy/reviewedAt), plus the matching case\'s status, applicant name/address, payee and account number. ' +
      'An empty possibleDuplicates array means no duplicates were found.',
    inputSchema: z.object({
      applicationId: z
        .uuid()
        .describe('GUID of the application to check (applicationId on search_cases rows / get_case, not the APP-… number and not a case GUID).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applications/${input.applicationId}/check-for-duplicates` }),
    selectResult: (raw) => ({
      originalAssistanceRequests: (raw?.originalAssistanceRequests ?? []).map(caseDetailTrimDuplicateRow),
      possibleDuplicates: (raw?.possibleDuplicates ?? []).map((d: any) => ({
        ...caseDetailTrimDuplicateRow(d),
        originalAssistanceRequestId: d.originalAssistanceRequestId,
        duplicateType: d.duplicateType,
        isNotDuplicate: d.isNotDuplicate,
        reviewedBy: caseDetailDisplayName(d.lastUpdatedBy),
        reviewedAt: d.lastUpdatedAt,
      })),
    }),
  },
  {
    name: 'check_ar_duplicate_address',
    toolset: 'case-detail',
    description:
      'Check whether OTHER applicants at the same street address already received help: searches accepted assistance requests from the last year at this licensed location ' +
      'for a similar address (street + apt + zip) belonging to a DIFFERENT applicant than the one given. ' +
      'Use to detect multiple household members applying separately from one address. ' +
      'Returns an array of matches, each with the accepted date, the other applicant (id, name, address with city/county/state), the matching application and assistance request GUIDs + public IDs, and the assistance type. ' +
      'An empty array means no other applicant at that address was found. For duplicates of the case itself (same applicant, payee account, name+DOB) use check_application_duplicates instead.',
    inputSchema: z.object({
      caseId: z.uuid().describe('GUID of the assistance request being reviewed (the id field from search_cases / get_case).'),
      applicantId: z
        .uuid()
        .describe('GUID of the applicant on that case (applicant.id from get_case, or id from search_applicants). Matches from this applicant are excluded — the search is for OTHER applicants at the same address.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/assistance-requests/${input.caseId}/check-for-duplicate-address/${input.applicantId}`,
    }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((m: any) => ({
        acceptedDate: m.acceptedDate,
        applicationId: m.applicationID ?? m.applicationId,
        applicationIdPublic: m.applicationIDPublic ?? m.applicationIdPublic,
        assistanceRequestId: m.assistanceRequestID ?? m.assistanceRequestId,
        assistanceRequestIdPublic: m.assistanceRequestIDPublic ?? m.assistanceRequestIdPublic,
        assistanceType: m.assistanceType?.name ?? m.assistanceType,
        applicant: m.applicant && {
          applicantId: m.applicant.applicantId,
          firstName: m.applicant.firstName,
          lastName: m.applicant.lastName,
          address: m.applicant.address,
        },
      })),
  },
  {
    name: 'list_ar_distributions',
    toolset: 'case-detail',
    description:
      'List the goods distributions recorded on a non-funded assistance request (food pantry / disaster / school-supplies cases): each visit where items were handed out or denied. ' +
      'Use for questions like "what did this applicant pick up", "when was their last pantry visit", or "was the distribution fulfilled". ' +
      'Returns a paged result { count, distributions[] } where each row has the pantry name, distributedAt date, status ' +
      '(Fulfilled, FulfilledExpired, FulfilledIneligible, DeniedExpired, DeniedIneligible, DeniedOther), type (Food, Disaster, SchoolSupplies), ' +
      'the distributed items with quantities and measuring units, household size served, notes, whether a signature was required/collected, and who recorded it. ' +
      'Only applies to non-funded assistance types — financial cases will have no distributions.',
    inputSchema: z.object({
      caseId: z.uuid().describe('GUID of the assistance request (the id field from search_cases / get_case, not the AR-… number).'),
      pantryName: z
        .string()
        .optional()
        .describe('Filter: only distributions from pantries whose name contains this text (case-sensitive contains).'),
      page: z.number().int().min(1).optional().describe('1-based page number. Default 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Default 20.'),
      sort: z
        .enum(['pantryName', 'distributedAt', 'status'])
        .optional()
        .describe('Sort column. Default distributedAt.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction. Default desc (most recent first).'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const query: Record<string, any> = {
        page: input.page ?? 1,
        pageSize: Math.min(input.pageSize ?? 20, 50),
        sort: input.sort ?? 'distributedAt',
        order: input.order ?? 'desc',
      };
      if (input.pantryName) {
        query['filters[0].Field'] = 'pantryName';
        query['filters[0].Value'] = input.pantryName;
        query['filters[0].Operation'] = 'Contains';
      }
      return { path: `/assistance-requests/${input.caseId}/distributions`, query };
    },
    selectResult: (raw) => {
      const rows: any[] = raw?.result ?? (Array.isArray(raw) ? raw : []);
      return {
        count: raw?.count ?? rows.length,
        distributions: rows.map((d) => ({
          id: d.id,
          pantryId: d.pantryId,
          pantryName: d.pantryName,
          distributedAt: d.distributedAt,
          status: d.status,
          type: d.type,
          totalHouseholdMembers: d.totalHouseholdMembers,
          items: (d.distributions ?? []).map((i: any) => ({
            title: i.title,
            quantity: i.quantity,
            measuringUnit: i.measuringUnitTitle,
          })),
          notes: d.notes,
          county: d.county,
          signatureRequired: d.signatureRequired,
          signedAt: d.signedAt,
          recordedBy: caseDetailDisplayName(d.user),
          volunteer: caseDetailDisplayName(d.volunteer),
          createdAt: d.createdAt,
          updatedAt: d.updatedAt,
        })),
      };
    },
  },
  {
    name: 'get_distribution_slots',
    toolset: 'case-detail',
    description:
      'Get the open appointment time slots at a specific pantry/distribution site for a non-funded assistance request (food, disaster, school supplies). ' +
      'Use before proposing a pickup/distribution appointment time for the applicant. ' +
      'Requires the case GUID and the pantry GUID (get pantry ids from the pantryId field on list_ar_distributions rows, or from the case\'s pantry options in the UI). ' +
      'Returns available slots as { startTime, endTime } pairs with which meeting modes each supports (virtual/phone/in-person) and the in-person address/notes. ' +
      'This is different from list_available_slots, which finds slots for staff-role meetings rather than pantry distributions.',
    inputSchema: z.object({
      caseId: z.uuid().describe('GUID of the assistance request the distribution belongs to (id from search_cases / get_case).'),
      pantryId: z.uuid().describe('GUID of the pantry / distribution location to get slots for (pantryId from list_ar_distributions rows).'),
      appointmentId: z
        .uuid()
        .optional()
        .describe('Existing distribution appointment GUID to exclude from conflict checks when rescheduling that appointment. Omit when booking a new one.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path:
        `/assistance-requests/${input.caseId}/distributions/${input.pantryId}/available-slots` +
        (input.appointmentId ? `/${input.appointmentId}` : ''),
    }),
    selectResult: (raw) => {
      const slots: any[] = Array.isArray(raw) ? raw : [];
      const MAX = 100;
      return {
        count: slots.length,
        truncated: slots.length > MAX,
        slots: slots.slice(0, MAX).map((s) => ({
          startTime: s.startTime,
          endTime: s.endTime,
          virtualEnabled: s.virtualEnabled,
          phoneEnabled: s.phoneEnabled,
          inPersonEnabled: s.inPersonEnabled,
          inPersonAddress: s.inPersonAddress,
          inPersonNotes: s.inPersonNotes,
        })),
      };
    },
  },
  {
    name: 'get_ar_summaries',
    toolset: 'case-detail',
    description:
      'List the generated summary documents attached to an assistance request: payment summaries, audit summaries, and FPL (Florida Power & Light) summaries. ' +
      'These are system-generated PDF reports of the case. Use when asked whether a payment/audit summary exists for a case or when it was generated. ' +
      'Metadata only — returns each summary\'s id, summaryType (payments | audit | fpl), description, createdAt and fileId; this tool cannot download or read the PDF contents. ' +
      'An empty array means no summaries of the requested type have been generated yet (generation is queued and may still be in progress).',
    inputSchema: z.object({
      caseId: z.uuid().describe('GUID of the assistance request (the id field from search_cases / get_case, not the AR-… number).'),
      type: z
        .enum(['payments', 'audit', 'fpl'])
        .optional()
        .describe('Only return summaries of this type. Omit to return summaries of every type.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/assistance-requests/${input.caseId}/summary`,
      ...(input.type ? { query: { type: input.type } } : {}),
    }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((s: any) => ({
        id: s.id,
        summaryType: s.summaryType,
        description: s.description,
        createdAt: s.createdAt,
        fileId: s.fileId,
      })),
  },
  {
    name: 'list_approval_signatures',
    toolset: 'case-detail',
    description:
      'Get the staff approval signature recorded on an assistance request, when the service area requires a signature to approve the assistance type. ' +
      'Use when asked who signed off on a case approval or whether the required approval signature has been collected. ' +
      'Returns the signature record — who signed (staff name, email, roleId), when (timestamp), from what IP address, and whether their saved profile signature was used — or null when no approval signature exists on the case. ' +
      'Metadata only: the signature image itself cannot be displayed.',
    inputSchema: z.object({
      caseId: z.uuid().describe('GUID of the assistance request (the id field from search_cases / get_case, not the AR-… number).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/assistance-requests/${input.caseId}/approval-signatures` }),
    selectResult: (raw) =>
      raw
        ? {
            id: raw.id,
            assistanceRequestId: raw.assistanceRequestId,
            signedAt: raw.timestamp,
            ipAddress: raw.ipAddress,
            isProfileSignature: raw.isProfileSignature,
            signedBy: caseDetailDisplayName(raw.userProfile),
            signedByRoleId: raw.userProfile?.roleId,
            signedByEmail: raw.userProfile?.userEmail,
          }
        : null,
  },
  {
    name: 'list_assignable_roles',
    toolset: 'case-detail',
    description:
      'List the staff members (role profiles) that appear as case assignees at the current admin\'s location — everyone currently assigned to at least one assistance request, plus staff saved in assignment filters. ' +
      'Use this to resolve a staff name to the numeric id needed by assign_case (its assignedTo parameter) or to interpret the assignedTo number on search_cases rows / get_case. ' +
      'Returns an array of staff entries with roleId (the number to pass as assignedTo), userId, display name, email, and location name. ' +
      'Note: this lists staff already used in assignments, not necessarily every assignable staff member — if the person is missing, ask the admin for their id.',
    inputSchema: z.object({}),
    method: 'GET',
    buildRequest: () => ({ path: '/assistance-requests/roles' }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((r: any) => ({
        roleId: r.roleId,
        userId: r.userId,
        name: caseDetailDisplayName(r),
        email: r.userEmail,
        locationName: r.locationName,
      })),
  },
];

// ---------------------------------------------------------------------------
// Scheduling + meetings + document-link domain tools
// Endpoints: SchedulerController / MeetingsController / MeetingSitesController /
// ApplicationsController (SAS link variants) in the .NET Admin API.
// ---------------------------------------------------------------------------

const schedulingTools: AgentToolDef[] = [
  {
    name: 'get_appointment',
    toolset: 'scheduling',
    description:
      'Fetch the full detail of a single scheduler appointment by its GUID id — the id field returned by list_appointments. ' +
      'Returns applicant identity (name, email, phone, public applicant/application IDs and GUIDs), the staff member the meeting is with ' +
      '(userFullName, userID, roleID), timing (startTime/endTime in UTC, length in minutes, timezone, requested valid window validTimeStart/validTimeEnd, expiresAt), ' +
      'status (Requested, Scheduled, CancelledByApplicant, CancelledByCaseworker, Complete, NoShow), meeting modality flags (isVirtual/isPhone/isInPerson ' +
      'plus which modalities were offered), in-person address/notes, the virtual meeting join URL when one was started, internal staff notes and ' +
      'external notes visible to the applicant, and created/updated timestamps. Use after list_appointments when the admin asks about one specific appointment.',
    inputSchema: z.object({
      appointmentId: z.uuid().describe('GUID of the appointment (the id field from list_appointments rows).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/scheduler/appointments/${input.appointmentId}` }),
    selectResult: (raw) => {
      if (!raw || typeof raw !== 'object') return raw;
      // translations = per-language UI strings; metadata = internal Dyte meeting tokens — neither useful to the model
      const { translations, metadata, ...rest } = raw;
      return rest;
    },
  },
  {
    name: 'list_available_locations',
    toolset: 'scheduling',
    description:
      'List the in-person meeting locations that have at least one bookable slot for a staff role within a time window. ' +
      'Use this to discover valid values for the inPersonLocation parameter of list_available_slots, or to answer "where can the applicant meet someone in person". ' +
      'Returns an array of locations, each with the in-person address (the exact string to pass to list_available_slots) and any staff notes about the location. ' +
      'Locations with no availability in the window are excluded — widen the window if the list comes back empty.',
    inputSchema: z.object({
      roleId: z
        .number()
        .int()
        .describe('Integer staff role ID to meet with (same value as the roleID field on appointments).'),
      startTime: z.string().describe('Search window start, ISO 8601 UTC timestamp (e.g. 2026-07-06T13:00:00.000Z).'),
      endTime: z.string().describe('Search window end, ISO 8601 UTC timestamp. A one-to-two-week window is typical.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: '/scheduler/available-locations',
      query: {
        roleId: input.roleId,
        moduleId: 9, // SAHelp module
        startTime: input.startTime,
        endTime: input.endTime,
      },
    }),
    selectResult: (raw) => {
      const locations: any[] = Array.isArray(raw) ? raw : (raw?.result ?? []);
      return locations.map((l) => ({
        inPersonAddress: l.inPersonAddress,
        inPersonNotes: l.inPersonNotes,
      }));
    },
  },
  {
    name: 'list_application_meetings',
    toolset: 'scheduling',
    description:
      'List the RECORDED video meeting sessions held on an application within the LAST 7 DAYS. ' +
      'Each row has the meeting GUID id (use with get_meeting_recording_url to get a playable link), the staff member who hosted the meeting, ' +
      'recording status and started/stopped times, and the participant name/IP captured from the recording consent. ' +
      'Only sessions that produced a recording appear; older or unrecorded sessions are not returned. ' +
      'Takes the application GUID (applicationId from search_cases rows or get_case), not a case or applicant GUID. ' +
      'Use when the admin asks whether a video interview happened recently or wants to review a session recording.',
    inputSchema: z.object({
      applicationId: z.uuid().describe('GUID of the application whose video sessions to list (applicationId from search_cases / get_case).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: '/meetings', query: { applicationId: input.applicationId } }),
    selectResult: (raw) => {
      const rows: any[] = raw?.result ?? (Array.isArray(raw) ? raw : []);
      return {
        count: raw?.count ?? rows.length,
        meetings: rows.map((m: any) => ({
          id: m.id,
          hostedBy: userDisplayName(m.admin),
          recordingStatus: m.recordingStatus,
          recordingStartedTime: m.recordingStartedTime,
          recordingStoppedTime: m.recordingStoppedTime,
          participantName: m.participantName,
          participantIpAddress: m.participantIpAddress,
        })),
      };
    },
  },
  {
    name: 'get_meeting_recording_url',
    toolset: 'scheduling',
    description:
      'Get a temporary download/playback URL for the video recording of a meeting session. ' +
      'Takes the meeting GUID id from list_application_meetings. Returns a signed URL that EXPIRES IN 1 HOUR — ' +
      'present it to the admin as a link to open promptly; do not store it or treat it as permanent. ' +
      'Fails if the recording has not finished uploading (recordingStatus must be UPLOADED). ' +
      'Note: calling this is audited — a file-access log entry is recorded against the application under the current user.',
    inputSchema: z.object({
      meetingId: z.uuid().describe('GUID of the meeting session (the id field from list_application_meetings rows).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/meetings/${input.meetingId}/recording` }),
    // Payload is the SAS URL string itself; wrap it so the model sees a labeled field with the expiry caveat
    selectResult: (raw) => ({ recordingUrl: raw, expiresInHours: 1 }),
  },
  {
    name: 'list_meeting_sites',
    toolset: 'scheduling',
    description:
      'List the configured meeting sites (physical offices where staff can host in-person appointments) for the current admin\'s licensed location. ' +
      'Returns a paged result { count, pages, sites[] } where each site has id, title, full street address (address1/address2/city/state/zip), ' +
      'latitude/longitude when geocoded, and created/updated dates. ' +
      'Use to answer "what offices do we have" or to cross-reference an appointment\'s in-person address with a named site. ' +
      'Note this is the site CONFIGURATION list — for locations that actually have open slots for a role, use list_available_locations instead.',
    inputSchema: z.object({
      page: z.number().int().min(1).optional().describe('1-based page number. Default 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Default 20.'),
      sort: z.enum(['title', 'city', 'state', 'zip', 'createdAt']).optional().describe('Column to sort by.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction, used with sort. Defaults to asc.'),
    }),
    method: 'GET',
    buildRequest: (input) => {
      const query: Record<string, any> = {
        page: input.page ?? 1,
        pageSize: Math.min(input.pageSize ?? 20, 50),
      };
      if (input.sort) {
        query.sort = input.sort;
        query.order = input.order ?? 'asc';
      }
      return { path: '/meeting-sites', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      sites: (raw?.result ?? []).map((s: any) => ({
        id: s.id,
        title: s.title,
        address1: s.address1,
        address2: s.address2,
        city: s.city,
        state: s.state,
        zip: s.zip,
        latitude: s.latitude,
        longitude: s.longitude,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
      })),
    }),
  },
  {
    name: 'get_application_document_link',
    toolset: 'scheduling',
    description:
      'Get a temporary download URL for one supporting document uploaded to an application (proof of income, ID, lease, etc.). ' +
      'First call list_application_documents to find the document id and file name, then call this with the application GUID and document GUID. ' +
      'Returns { url, fileName, mimeType } — the url is a signed link that expires shortly; present it to the admin to open promptly, never store it. ' +
      'This tool only produces a link — it cannot read or summarize the document contents.',
    inputSchema: z.object({
      applicationId: z.uuid().describe('GUID of the application the document belongs to (applicationId from search_cases / get_case).'),
      documentId: z.uuid().describe('GUID of the document (the id field from list_application_documents rows).'),
      fileName: z
        .string()
        .optional()
        .describe('Optional friendly file name for the download link. Defaults to the stored file name.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/applications/${input.applicationId}/documents/${input.documentId}/sas`,
      query: input.fileName ? { fileName: input.fileName } : undefined,
    }),
    selectResult: (raw) =>
      raw && {
        url: raw.url,
        fileName: raw.fileName,
        mimeType: raw.mimeType,
      },
  },
  {
    name: 'get_application_file_link',
    toolset: 'scheduling',
    description:
      'Get a temporary download URL for a file attached to an application by its file GUID — e.g. a proof-of-payment upload or another file id ' +
      'referenced on the case/application record. Use get_application_document_link instead when the file came from the supporting-documents list. ' +
      'Returns { url, fileName, mimeType } — the url is a signed link that expires shortly; present it to the admin to open promptly, never store it. ' +
      'Note: calling this is audited — a file-access log entry is recorded against the application under the current user. ' +
      'This tool only produces a link — it cannot read or summarize the file contents.',
    inputSchema: z.object({
      applicationId: z.uuid().describe('GUID of the application the file belongs to (applicationId from search_cases / get_case).'),
      fileId: z.uuid().describe('GUID of the file attached to the application (a file id referenced on the case or application record).'),
      fileName: z
        .string()
        .optional()
        .describe('Optional friendly file name for the download link. Defaults to the stored file name.'),
      fileDescription: z
        .string()
        .optional()
        .describe('Optional human-readable description recorded in the file-access audit log, e.g. "Proof of Payment".'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: `/applications/${input.applicationId}/file/${input.fileId}/sas`,
      query: {
        // isFromPreview=true makes the API write the ApplicationFileAccessLog audit entry for this access
        isFromPreview: true,
        ...(input.fileName ? { fileName: input.fileName } : {}),
        ...(input.fileDescription ? { fileDescription: input.fileDescription } : {}),
      },
    }),
    selectResult: (raw) =>
      raw && {
        url: raw.url,
        fileName: raw.fileName,
        mimeType: raw.mimeType,
      },
  },
];

// ---------------------------------------------------------------------------
// Comms domain tools
// Endpoints: MassCommunicationsController / ApplicationsController (chat) /
// ApplicantsController (bulk-notifications).
// ---------------------------------------------------------------------------

const commsTools: AgentToolDef[] = [
  {
    name: 'list_mass_communications',
    toolset: 'comms',
    description:
      'List the mass communications (bulk email and bulk SMS blasts) sent from the current licensed location, newest first. ' +
      'Use for questions like "what mass emails went out last month", "who sent the last SMS blast", or "how many people received it". ' +
      'Returns a paged result { count, pages, communications[] } where each row has id (use with get_mass_communication for the full body), ' +
      'createdAt, type ("email" or "sms"), preview (the email subject, or the first 50 characters of the SMS text), totalRecipients, ' +
      'sentBy (staff display name), audienceFilters (human-readable description of the recipient filters used), and message. ' +
      'The preview is truncated — call get_mass_communication with the row id to read the full message body.',
    inputSchema: z.object({
      type: z
        .enum(['email', 'sms'])
        .optional()
        .describe('Only communications of this type: "email" = bulk emails, "sms" = bulk text messages. Omit for both.'),
      textContains: z
        .string()
        .optional()
        .describe(
          'Case-sensitive substring match on the message: matches the subject line of emails and the body text of SMS messages.'
        ),
      minTotalRecipients: z
        .number()
        .int()
        .optional()
        .describe('Only communications sent to at least this many recipients.'),
      maxTotalRecipients: z
        .number()
        .int()
        .optional()
        .describe('Only communications sent to at most this many recipients.'),
      createdAfter: z
        .string()
        .optional()
        .describe('Only communications sent on/after this UTC ISO timestamp, e.g. "2026-06-01T00:00:00Z".'),
      createdBefore: z
        .string()
        .optional()
        .describe(
          'Only communications sent on/before this UTC ISO timestamp. For an inclusive "on or before <date>" bound, pass the start of the NEXT day.'
        ),
      page: z.number().int().min(1).optional().describe('1-based page number. Default 1.'),
      pageSize: z
        .number()
        .int()
        .min(1)
        .max(20)
        .optional()
        .describe('Rows per page, max 20. Default 10. Use 1 with the returned count for "how many" questions.'),
      sort: z
        .enum(['createdAt', 'type', 'totalRecipients'])
        .optional()
        .describe('Sort column. Default "createdAt".'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction. Default "desc" (newest first).'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => {
      const { query, add } = filterQuery({
        page: input.page ?? 1,
        pageSize: Math.min(input.pageSize ?? 10, 20),
        sort: input.sort ?? 'createdAt',
        order: input.order ?? 'desc',
      });
      if (input.type) add('type', input.type, 'In', 'Communication Type');
      // 'body' is a special server-side filter: subject for emails, body text for SMS
      if (input.textContains) add('body', input.textContains, 'Contains', 'Body');
      if (input.minTotalRecipients != null)
        add('totalRecipients', input.minTotalRecipients, 'IsGreaterThanOrEqualTo', 'Total Recipients');
      if (input.maxTotalRecipients != null)
        add('totalRecipients', input.maxTotalRecipients, 'IsLessThanOrEqualTo', 'Total Recipients');
      if (input.createdAfter) add('createdAt', input.createdAfter, 'IsGreaterThanOrEqualTo', 'Created Date');
      if (input.createdBefore) add('createdAt', input.createdBefore, 'IsLessThanOrEqualTo', 'Created Date');
      return { path: '/mass-communications', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      communications: (raw?.result ?? []).map((m: any) => ({
        id: m.id,
        createdAt: m.createdAt,
        type: m.type,
        preview: m.text,
        totalRecipients: m.totalRecipients,
        // DTO property is RankedUserNam (sic) — serialized as rankedUserNam
        sentBy: m.sentBy
          ? m.sentBy.rankedUserNam || [m.sentBy.firstName, m.sentBy.lastName].filter(Boolean).join(' ') || undefined
          : undefined,
        audienceFilters: m.filtersUsed,
        message: m.message,
      })),
    }),
  },
  {
    name: 'get_mass_communication',
    toolset: 'comms',
    description:
      'Get the full message body of one mass communication by its GUID id (from list_mass_communications or get_applicant_bulk_notifications rows). ' +
      'Returns the complete text: raw HTML for bulk emails, plain text for bulk SMS. Long bodies are truncated to keep responses small. ' +
      'Use after list_mass_communications when the preview is not enough to answer what was actually sent.',
    inputSchema: z.object({
      id: z.uuid().describe('Mass communication GUID (the id field from list_mass_communications rows).'),
    }),
    method: 'GET',
    requiredPermission: 'CanManageSAHelp',
    buildRequest: (input) => ({ path: `/mass-communications/${input.id}` }),
    selectResult: (raw) => {
      // Endpoint returns the body as a bare string inside the ApiResult envelope
      const body = typeof raw === 'string' ? raw : '';
      const MAX = 6000;
      return {
        body: body.length > MAX ? `${body.slice(0, MAX)}… (truncated)` : body,
        truncated: body.length > MAX,
      };
    },
  },
  {
    name: 'list_pending_chats',
    toolset: 'comms',
    description:
      'Get the applicant-chat notification queue: unread chat messages from applicants, limited to applications in the current user\'s ' +
      'service areas AND to conversations the current admin has already participated in. Use when the admin asks "do I have any unanswered ' +
      'applicant chats" or "who is waiting on a chat reply". Returns an array of { applicationId (GUID — use with get_application), ' +
      'applicantName, applicationNumber (the numeric public APP number) }. ' +
      'CAUTION: this is a notification poll with a side effect — fetching it marks the messages as notified, and each pending message only ' +
      'appears in the feed for about 15 seconds after it is first retrieved. An empty result therefore does NOT prove there are no unread ' +
      'chats; it may simply mean they were already surfaced. Do not call it repeatedly in one turn.',
    inputSchema: z.object({}),
    method: 'GET',
    buildRequest: () => ({ path: '/applications/chat/notification' }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((c: any) => ({
        applicationId: c.applicationId,
        applicantName: c.applicantName,
        applicationNumber: c.applicationNumber,
      })),
  },
  {
    name: 'get_applicant_bulk_notifications',
    toolset: 'comms',
    description:
      'List the bulk notifications (mass communications) a specific applicant has received, of one type at a time: "email" or "sms". ' +
      'Use to answer "what mass emails/texts did this applicant get and when". Returns an array ordered oldest first with id, ' +
      'massCommunicationId (use with get_mass_communication for the full untruncated body), type, subject (emails only, empty for SMS), ' +
      'a truncated body preview, and createdAt (when it was sent to this applicant).',
    inputSchema: z.object({
      id: z.uuid().describe('Applicant GUID (the id from search_applicants or get_applicant — not the numeric public ID).'),
      type: z
        .enum(['email', 'sms'])
        .describe('Which notification channel to list: "email" for bulk emails, "sms" for bulk text messages. Call once per channel if both are needed.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/applicants/${input.id}/bulk-notifications/${input.type}` }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((r: any) => {
        const body = typeof r.body === 'string' ? r.body : '';
        const MAX = 300;
        return {
          id: r.id,
          massCommunicationId: r.massCommunicationID,
          type: r.type,
          subject: r.subject,
          bodyPreview: body.length > MAX ? `${body.slice(0, MAX)}… (truncated)` : body,
          createdAt: r.createdAt,
        };
      }),
  },
];

// ---------------------------------------------------------------------------
// Reference lookup tools (comms-adjacent)
// Endpoints: CountiesController / LicensedLocationsController / TranslationsController.
// ---------------------------------------------------------------------------

const commsReferenceTools: AgentToolDef[] = [
  {
    name: 'list_counties',
    toolset: 'reference',
    description:
      'List the county names covered by the current user\'s licensed location (derived from its service-area ZIP codes), alphabetized. ' +
      'This resolves the valid values for the "county" filter of search_cases — call it first when the user filters cases by county, ' +
      'to match their wording to the exact county names the API expects. Takes no parameters and returns a plain array of county name strings.',
    inputSchema: z.object({}),
    method: 'GET',
    buildRequest: () => ({ path: '/counties/list' }),
    selectResult: (raw) => (Array.isArray(raw) ? raw : []),
  },
  {
    name: 'list_declined_reasons',
    toolset: 'reference',
    description:
      'List the application declined reasons configured for the current licensed location. ' +
      'This resolves the GUID values for the "declinedReasonId" filter of search_cases — call it first when the user asks about declined ' +
      'cases by reason (e.g. "cases declined for incomplete documents"), then pass the matching id(s) to search_cases. ' +
      'Returns an array of { id, reason, active, isOther, displayOrder }.',
    inputSchema: z.object({
      active: z
        .boolean()
        .optional()
        .describe('Filter to active (true) or inactive (false) reasons. Omit to include both.'),
      includeDeleted: z
        .boolean()
        .optional()
        .describe('true = also include soft-deleted reasons (useful when resolving the decline reason of an old case). Default false.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: '/licensed-locations/application-declined-reasons',
      query: {
        ...(input.active != null ? { activeStatus: input.active } : {}),
        includeDeleted: input.includeDeleted ?? false,
      },
    }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((r: any) => ({
        id: r.id,
        reason: r.reason,
        active: r.active,
        isOther: r.isOther,
        displayOrder: r.displayOrder,
        ...(r.deletedAt ? { deletedAt: r.deletedAt } : {}),
      })),
  },
  {
    name: 'list_languages',
    toolset: 'reference',
    description:
      'List all active languages the system supports. ' +
      'This resolves the GUID values for the "submittedLanguageId" filter of search_cases — call it first when the user asks about cases ' +
      'by submission language (e.g. "applications submitted in Spanish"), then pass the matching id(s) to search_cases. ' +
      'Returns an array of { id, languageCode (e.g. "es"), languageName (English name), languageNameNative }.',
    inputSchema: z.object({}),
    method: 'GET',
    buildRequest: () => ({ path: '/translations/languages/all' }),
    selectResult: (raw) =>
      // Response includes each language's full UI-translation dictionary — drop it, keep identifiers only
      (Array.isArray(raw) ? raw : []).map((l: any) => ({
        id: l.id,
        languageCode: l.languageCode,
        languageName: l.languageName,
        languageNameNative: l.languageNameNative,
      })),
  },
];

// ---------------------------------------------------------------------------
// Reference + oversight + WellSky domain tools
// Endpoints: ServiceAreasController / DashboardController (nss-report) /
// OrgStatsController / IntegrationsController in the .NET Admin API.
// ---------------------------------------------------------------------------

const referenceOversightTools: AgentToolDef[] = [
  {
    name: 'get_service_area',
    toolset: 'reference',
    description:
      'Get the full configuration of one service area by its GUID id (resolve names or ZIP codes to ids with list_service_areas first). ' +
      'Use when a question needs service-area details beyond the list row: contact email/phone, weekly/monthly application intake limits, per-assistance-type limits, ' +
      'e-signature-before-approval settings, external integration type (e.g. WellSky), attached funds, in-person meeting sites, covered ZIP codes, or the owning location ids. ' +
      'Returns the service area with active flag, contact info, application/assistance-type limit configurations, requireApprovalSignature settings, ' +
      'zipCodeCount plus the ZIP list (truncated after 60), funds [{ id, name, active }] and meetingSites. ' +
      'Fails with 401 if the current user has no access role for that service area.',
    inputSchema: z.object({
      serviceAreaId: z.uuid().describe('GUID of the service area (the id field from list_service_areas).'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/service-areas/${input.serviceAreaId}` }),
    selectResult: (raw) => {
      if (!raw || typeof raw !== 'object') return raw;
      const zipCodes: string[] = Array.isArray(raw.zipCodes) ? raw.zipCodes : [];
      const MAX_ZIPS = 60;
      return {
        id: raw.id,
        name: raw.name,
        active: raw.active,
        toggleActiveDate: raw.toggleActiveDate,
        createdAt: raw.createdAt,
        contactEmail: raw.contactEmail,
        contactPhone: raw.contactPhone,
        infoUrl: raw.infoUrl,
        locationId: raw.locationId,
        licensedLocationId: raw.licensedLocationId,
        associatedLocationIds: raw.associatedLocationIds,
        externalIntegrationType: raw.externalIntegrationType,
        hasActiveNonEmptyFunds: raw.hasActiveNonEmptyFunds,
        applicationLimitConfiguration: raw.applicationLimitConfiguration,
        enablePerAssistanceTypeLimits: raw.enablePerAssistanceTypeLimits,
        assistanceTypesLimitConfiguration: raw.assistanceTypesLimitConfiguration,
        requireApprovalSignature: raw.requireApprovalSignature,
        zipCodeCount: zipCodes.length,
        zipCodes: zipCodes.length > MAX_ZIPS ? [...zipCodes.slice(0, MAX_ZIPS), `… +${zipCodes.length - MAX_ZIPS} more`] : zipCodes,
        funds: (raw.serviceAreaFunds ?? []).map((sf: any) => ({
          id: sf?.fund?.id,
          name: sf?.fund?.name,
          active: sf?.fund?.active,
        })),
        meetingSites: (raw.meetingSites ?? []).map((m: any) => ({ id: m?.id, name: m?.name, address: m?.address })),
      };
    },
  },
  {
    name: 'get_zip_service_area',
    toolset: 'reference',
    description:
      'Look up which service area covers a single ZIP code. Faster and more direct than list_service_areas when the question is exactly "what service area / location handles ZIP x". ' +
      'Returns { serviceAreaId, serviceAreaName, licensedLocationId, licensedLocationName } when the ZIP is covered by a service area the current user can access; ' +
      'returns { found: false } when no service area covers the ZIP, and errors with 401 when the covering area belongs to a location the user cannot access. ' +
      'Use the returned serviceAreaId with get_service_area or other tools that need a service area GUID.',
    inputSchema: z.object({
      zipCode: z
        .string()
        .regex(/^\d{5}$/, 'Must be exactly 5 digits')
        .describe('A single 5-digit ZIP code to look up, e.g. "78205". One ZIP per call — do not comma-separate.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({ path: `/service-areas/${input.zipCode}/get-details` }),
  },
  {
    name: 'get_nss_report',
    toolset: 'reference',
    description:
      'Get the internal NSS (National Statistical System) report: read-only monthly program statistics rolled up for the current user\'s location(s). ' +
      'Use for questions like "what are our stats for March" or "show the NSS numbers for last month". ' +
      'This is a read-only query even though it uses POST — it changes nothing. ' +
      'Returns an array of rows { nssCode, nssColumnDesc, nssValue } — nssCode is the NSS statistical code, nssColumnDesc the human-readable measure name, nssValue the numeric value for the requested month.',
    inputSchema: z.object({
      month: z.number().int().min(1).max(12).describe('Calendar month to report on, 1 (January) through 12 (December).'),
      year: z
        .number()
        .int()
        .min(2023)
        .describe('Calendar year to report on. Must be 2023 or later and not in the future (the API rejects years after the current year).'),
      includedDownLine: z
        .boolean()
        .optional()
        .describe('true = also include statistics from child (down-line) locations under the selected locations. Default false.'),
      locations: z
        .array(z.number().int())
        .optional()
        .describe('Legacy numeric location IDs to report on (locId values from list_org_locations). Omit to default to the current user\'s own location.'),
      programFormIds: z
        .array(z.uuid())
        .optional()
        .describe('Restrict the report to these program form GUIDs. Omit for all programs. The call fails if any id does not exist.'),
      timeZone: z
        .string()
        .optional()
        .describe('IANA time zone used to bound the month, e.g. "America/Chicago". Omit to use the API default.'),
    }),
    method: 'POST',
    buildRequest: (input) => ({
      path: '/dashboard/nss-report',
      body: {
        month: input.month,
        year: input.year,
        includedDownLine: input.includedDownLine ?? false,
        ...(input.locations?.length ? { locations: input.locations } : {}),
        ...(input.programFormIds?.length ? { programFormIds: input.programFormIds } : {}),
        ...(input.timeZone ? { timeZone: input.timeZone } : {}),
      },
    }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((r: any) => ({
        nssCode: r.nssCode,
        nssColumnDesc: r.nssColumnDesc,
        nssValue: r.nssValue,
      })),
  },
  {
    name: 'list_org_locations',
    toolset: 'oversight',
    description:
      'Oversight-only: get the organizational hierarchy visible to the current user — territories, divisions, SAHelp-licensed locations (with their division/territory), ' +
      'and every service area under those locations. Use to answer "what locations/divisions do we oversee", to resolve a location name to its numeric locId ' +
      '(needed by get_nss_report locations), or to map service areas to licensed locations before calling list_oversight_funds. ' +
      'Takes no parameters. Returns { territories: string[], divisions: [{ divisionName, territoryName }], ' +
      'licensedLocations: [{ locId, locName, divisionName, territoryName }], serviceAreas: [{ id, name, locationId, licensedLocationId }] }. ' +
      'Only locations that have at least one service area are included.',
    inputSchema: z.object({}),
    method: 'GET',
    requiredPermission: 'SAHelpOversight',
    buildRequest: () => ({ path: '/org-stats/locations' }),
    selectResult: (raw) =>
      raw && {
        territories: raw.territories ?? [],
        divisions: (raw.divisions ?? []).map((d: any) => ({
          divisionName: d.divisionName,
          territoryName: d.territoryName,
        })),
        licensedLocations: (raw.licensedLocations ?? []).map((l: any) => ({
          locId: l.locId,
          locName: l.locName,
          divisionName: l.divisionName,
          territoryName: l.territoryName,
        })),
        serviceAreas: (raw.serviceAreas ?? []).map((s: any) => ({
          id: s.id,
          name: s.name,
          locationId: s.locationId,
          licensedLocationId: s.licensedLocationId,
        })),
      },
  },
  {
    name: 'list_oversight_funds',
    toolset: 'oversight',
    description:
      'Oversight-only: list funds with balances across ALL locations the current user oversees (their location plus every SAHelp-licensed child location) — ' +
      'a wider view than list_funds, which is scoped to one licensed location. Use for organization-wide money questions like ' +
      '"fund balances across the division" or "which funds under us are running low". ' +
      'Returns a paged result { count, pages, funds[] } where each fund has id, name, active, fundingSourceId/fundingSourceName, serviceAreaNames, and balances converted to US dollars: ' +
      'balanceAllocatedUsd (current allocation), balancePendingUsd + balanceApprovedUsd (encumbered), balanceFundedUsd (issued payments), ' +
      'balanceAvailableUsd (unspent = allocation minus pending, approved and issued), currentBalanceUsd (allocation minus issued payments). ' +
      'IMPORTANT: the chat UI automatically renders this result as a rich table — do NOT repeat the rows as a markdown table or list, and do NOT claim "the list above" unless you actually called this tool this turn. Reply with a one-line summary and any insight.',
    inputSchema: z.object({
      name: z.string().optional().describe('Case-insensitive partial match on the fund name.'),
      active: z.boolean().optional().describe('Filter to active (true) or inactive (false) funds. Omit to include both.'),
      fundingSourceId: z
        .uuid()
        .optional()
        .describe('Only funds drawing from this funding source (GUID).'),
      serviceAreaIds: z
        .array(z.uuid())
        .optional()
        .describe('Only funds attached to any of these service area GUIDs (get ids from list_org_locations or list_service_areas).'),
      page: z.number().int().min(1).optional().describe('1-based page number. Defaults to 1.'),
      pageSize: z.number().int().min(1).max(50).optional().describe('Rows per page, max 50. Defaults to 20.'),
      sort: z
        .enum([
          'name',
          'fundingSourceName',
          'balanceAllocated',
          'balancePending',
          'balanceFunded',
          'balanceAvailable',
          'currentBalance',
          'createdAt',
        ])
        .optional()
        .describe('Column to sort by. Balance columns sort by raw amount.'),
      order: z.enum(['asc', 'desc']).optional().describe('Sort direction, used with sort. Defaults to asc.'),
    }),
    method: 'GET',
    requiredPermission: 'SAHelpOversight',
    buildRequest: (input) => {
      const { query, add } = filterQuery(pagedParams(input));
      if (input.sort) {
        query.sort = input.sort;
        query.order = input.order ?? 'asc';
      }
      if (input.name) add('name', input.name, 'Contains');
      if (typeof input.active === 'boolean') add('active', String(input.active), 'IsEqualTo');
      if (input.fundingSourceId) add('fundingSourceID', input.fundingSourceId, 'In');
      if (input.serviceAreaIds?.length) add('serviceAreaIds', input.serviceAreaIds.join(','), 'In');
      return { path: '/org-stats/funds', query };
    },
    selectResult: (raw) => ({
      count: raw?.count ?? 0,
      pages: raw?.pages ?? null,
      funds: (raw?.result ?? []).map((f: any) => ({
        id: f.id,
        name: f.name,
        active: f.active,
        fundingSourceId: f.fundingSourceID ?? f.fundingSourceId,
        fundingSourceName: f.fundingSourceName,
        serviceAreaNames: f.serviceAreaNames ?? [],
        balanceAllocatedUsd: centsToUsd(f.balanceAllocated),
        balancePendingUsd: centsToUsd(f.balancePending),
        balanceApprovedUsd: centsToUsd(f.balanceApproved),
        balanceFundedUsd: centsToUsd(f.balanceFunded),
        balanceAvailableUsd: centsToUsd(f.balanceAvailable),
        currentBalanceUsd: centsToUsd((f.balanceAllocated ?? 0) - (f.balanceFunded ?? 0)),
        createdAt: f.createdAt,
      })),
    }),
  },
  {
    name: 'search_wellsky_clients',
    toolset: 'wellsky',
    description:
      'Search the external WellSky (Community Services) system for client records, either by exact WellSky client ID or by first name + last name + date of birth ' +
      '(all three are required together for a name search — WellSky matches on all of them). ' +
      'Use when checking whether an applicant already exists in WellSky before linking or creating records there. ' +
      'Read-only; only works when the WellSky integration is enabled and configured for the current location — otherwise the call fails with an error. ' +
      'Returns an array of matching clients (empty when none): { clientId, firstName, middleName, lastName, suffix, dateOfBirth, isActive, clientDateAdded, clientDateUpdated, households: [{ householdId, dateJoined }] }.',
    inputSchema: z.object({
      clientId: z
        .number()
        .int()
        .positive()
        .optional()
        .describe('Exact WellSky client ID. When provided, the name/DOB fields are ignored.'),
      firstName: z
        .string()
        .optional()
        .describe('Client first name, exact match in WellSky. Required (with lastName and dateOfBirth) when clientId is not given.'),
      lastName: z
        .string()
        .optional()
        .describe('Client last name, exact match in WellSky. Required (with firstName and dateOfBirth) when clientId is not given.'),
      dateOfBirth: z
        .string()
        .optional()
        .describe('Client date of birth as YYYY-MM-DD, e.g. "1985-03-24". Required (with firstName and lastName) when clientId is not given.'),
    }),
    method: 'GET',
    buildRequest: (input) => ({
      path: '/integrations/wellsky/clients',
      query: {
        ...(input.clientId != null ? { clientId: input.clientId } : {}),
        ...(input.firstName ? { firstName: input.firstName } : {}),
        ...(input.lastName ? { lastName: input.lastName } : {}),
        ...(input.dateOfBirth ? { dateOfBirth: input.dateOfBirth } : {}),
      },
    }),
    selectResult: (raw) =>
      (Array.isArray(raw) ? raw : []).map((c: any) => ({
        clientId: c.clientId,
        firstName: c.firstName,
        middleName: c.middleName,
        lastName: c.lastName,
        suffix: c.suffix,
        dateOfBirth: c.svpprofdob,
        isActive: c.isActive,
        clientDateAdded: c.clientDateAdded,
        clientDateUpdated: c.clientDateUpdated,
        households: (c.households ?? []).map((h: any) => ({ householdId: h.householdId, dateJoined: h.dateJoined })),
      })),
  },
];

// ---------------------------------------------------------------------------
// Navigation tools — no API call; executed entirely client-side via
// CLIENT_TOOL_HANDLERS in utils/agent-tool-executor.ts (SPA router.push, so
// the chat window and conversation stay open across the navigation).
// ---------------------------------------------------------------------------

export const NAV_PAGES = [
  'dashboard',
  'applicants',
  'cases',
  'appointments',
  'funds',
  'funding-sources',
  'service-areas',
  'reports',
  'maps',
  'schedule',
] as const;

const navigationTools: AgentToolDef[] = [
  {
    name: 'navigate_to',
    toolset: 'core',
    description:
      'Navigate the admin dashboard in this tab to a record or page. Use whenever the user says "go to", "open", "take me to", "pull up", or "show me the page for" something. The chat stays open across navigation. ' +
      'Targets: applicant (needs id = applicant GUID), application (needs id = application GUID), case (needs applicationId = application GUID, plus optionally id = the case GUID to focus that assistance request), page (needs page = a named list page). ' +
      'You must resolve GUIDs first when the user gives a public ID or name: "AR-10-002-418-381" → search_cases with filter idPublic IsEqualTo 10002418381 (digits only), "SS-…" or a name → search_applicants; the search rows include the GUIDs. ' +
      'Returns { navigated: true, url } on success — confirm to the user in one short sentence.',
    inputSchema: z.object({
      target: z.enum(['applicant', 'application', 'case', 'page']).describe('What to navigate to.'),
      id: z
        .uuid()
        .optional()
        .describe('GUID of the applicant or application; for target "case", the case (assistance request) GUID.'),
      applicationId: z.uuid().optional().describe('Application GUID — required for target "case".'),
      page: z.enum(NAV_PAGES).optional().describe('Named list page — required for target "page". "cases" is the assistance-request queue.'),
    }),
  },
];

export const AGENT_TOOLS: AgentToolDef[] = [
  ...navigationTools,
  emailMeTool,
  ...applicantsTools,
  ...casesTools,
  ...dashboardSchedulerTools,
  ...fundsConfigTools,
  ...staffTools,
  ...timelineTools,
  ...moneyTools,
  ...caseDetailTools,
  ...schedulingTools,
  ...commsTools,
  ...commsReferenceTools,
  ...referenceOversightTools,
];
